// ============================================================================
// IMPORTS SECTION
// ============================================================================
// React core
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
// Routing
import { Link, useParams, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
// Type definitions
import { TaxRate, TaxRule, TaxGroup, Office, License, Order, Agent, Product, PaymentMethod, Addresses, PaymentGateway, ShippingCost } from "types";
import { getAuthCookie } from "../../utils/authCookies";
import { mapFoundationMembershipRowToOption } from "../../utils/orderPartyIdentity";
import { studioBatchVariantPreviewsFromCartLines } from "../../utils/signageStudioBatchRestore";
import {
  buildCartHeaderFromCart,
  buildPerItemCouponMapFromCartItems,
  getInitialShippingSelectionFromOrder,
  getInitialSpecialFeeIdsFromOrder,
  getLineItemsFromCart,
  getOrderIdFromResponse,
  getOrderNumberFromResponse,
  getResumeOrderIdFromCart,
  getResumeOrderNumberFromCart,
  linkCartToDraftOrder,
  getShippingRowsFromOrder,
  getSpecialFeeRowsFromOrder,
  mapOrderShippingRowsForPicker,
  type InitialShippingSelection,
} from "../../utils/checkoutCartOrderLink";
// Form management
import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
import { phoneValidation } from "validationSchemas/common";
// UI Components - Select/Dropdown
import Select from "react-select";
import { SingleValue } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
// Google Maps integration
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
// Stripe payment integration
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import PaymentForm from "../../components/PaymentForm";
// Redux state management
// Context providers
// Custom components
import ProductPickerModal from "components/ordercheckoutpop";
import AddressFormModal from "components/AddAddressFormModal";
import { UIReactSelectPro, UIInputPro } from "components/OrdersSelectInput";
import Instructions from "components/Instructions";
import PaymentPanel from "components/PaymentOptionsCard";
import PaymentFormManualOrder from "components/PaymentFormManualOrder";
import PaymentMethods from "components/PaymentMethods";
import { MobileCardsOnly } from "components/TableandMobilecomponent";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import InstructionsCreateCard from "components/Notes";
// Tax & Shipping components
import ShippingOptions, { type ShippingOptionsV2Handle } from "components/Tax/ShippingOptionsui";
import PONumber from "components/PONumber";
import SpecialFeePicker, { type SpecialFeePickerV2Handle } from "components/Tax/SpecialFeeui";
import { useCouponExpirationCheck } from "components/Tax/CouponExpirationCheck";
// Table components
import { useReactTable, getCoreRowModel, createColumnHelper, type ColumnDef } from "@tanstack/react-table";
// Assets
import ProductSelection from "assets/images/brands/ProductSelection.png";
import imagecustomRounded from "assets/images/imagecustomrounded.png";
import upslogo from "assets/images/brands/ups.png";
import OrderNotes from "assets/images/brands/OrderNotes.png";
// Utilities
import { showSuccess, showError } from "toast/toast";
import { useAppDispatch } from "../../redux/hooks";
import { clearCart } from "../../redux/slices/cartSlice";
import { useCart } from "components/CartContext";
import { CheckoutPaymentModal, clearCheckoutCartAfterPayment } from "components/checkout";
import { handleApiError } from "utils/errorHandler";
import { safeConsole, createSafeNavigate } from "utils/envUtils";
import { sendSalesOrderTriggerEmail } from "utils/salesOrderTriggerEmailEngine";
import { getSalesResolveDiscountCode } from "utils/salesResolveDiscountCode";
import { formatShippingChargeOrderTotalsLabel } from "utils/orderTotalsChargeLabels";
import { buildSpecialFeeCostBreakdownDisplay } from "utils/specialFeeCostBreakdownDisplay";
import { DiscountAmountDisplay, getAgentRealtorCheckoutLinePricing } from "components/DiscountLabelBadge";
import { OrderDiscountCodeField } from "components/orderResolve";
import {
  postSalesOrderResolve,
  buildSalesResolvePayload,
  applySalesResolveResponse,
  overlaySalesSubmitFromResolved,
  mapCheckoutCartToResolveCatalogRows,
  mergeCheckoutCartFromResolved,
  perItemCouponMapFromResolved,
  pickRealtorCheckoutResolveBuyer,
  buildCheckoutOrderPartyFields,
  getCheckoutOrderChargeFallbacks,
  readCheckoutCartLineTaxable,
  getCheckoutLineItemId,
  finalizeSalesOrderTaxAfterPaid,
  fetchSalesOrderForPaidFinalize,
  type ResolvedSalesOrder,
} from "orderResolve";
import {
  resolveShippingTaxableForPayload,
  resolveSpecialFeeTaxableForPayload,
  resolveSpecialFeeRequiredForPayload,
} from "orderResolve/utils/readResolveTaxable";
import {
  fetchOrderById,
  createOrder,
  updateOrder,
  fetchCartById,
  fetchUserAddresses,
  fetchOfficeAddresses,
  unwrapAddressListResponse,
  fetchPaymentGatewayBySupplier,
  fetchShippingCosts,
  fetchPaymentMethodsBySupplierAndUser,
  postOrderPayment,
  uploadDocumentsToOrder,
  searchUsers,
  searchUsersDatatable,
  fetchOfficeSelect,
  fetchOfficeTeamMembersSelect,
  fetchAgentSelect,
  resolveSupplierOrganizationId,
  persistSupplierOrganizationId,
  resolveBrokerageOrganizationId,
  resolveMembershipAddressesRouteId,
  resolveBrokerageFirmIdForAgentSearch,
  fetchOfficeById,
  resolveCheckoutSellerFoundationIds,
  readCartFoundationIds,
  resolveCheckoutVendorOrganizationId,
  sanitizeOrganizationId,
  resolvePaymentMethodsMembershipId,
  SALES_ORDER_API_BASE,
  salesOrderUrls,
} from "api/salesOrderApi";
// TYPE DEFINITIONS SECTION
// ============================================================================
// Product object structure for API responses
type ProductObj = {
  product_id: string | number;
  product: {
    variants?: Array<{ variant_id: string | number; package_dimensions?: any }>;
    package_dimensions?: any;
  };
};

// Package dimensions for shipping calculations
type PackageDimensions = {
  length: number;
  width: number;
  height: number;
  weight: number;
  weight_type?: string; // Weight unit type (e.g., "lbs", "kg")
};

// Line item tax information structure
type LineTax = {
  tax_percentage: number;
  unit_product_tax_amount: number;
  total_product_tax_amount: number;
  taxable: boolean;
  tax_details?: {
    combined_rate?: number;
    tax_amount?: number;
    applied_state?: string;
  };
};

// Flexible line tax type - can be single, array, or null/undefined
type MaybeLineTax = LineTax | LineTax[] | null | undefined;

// Cart item structure - represents a product variant in the shopping cart
interface CartItem {
  product_id: string;
  variant_id: string;
  warehouse_id: string;
  name: string;
  product_name: string;
  is_production?: boolean;
  sku: string;
  unit_purchase_price: number;
  unit_retail_price: number;
  changed_unit_price?: number; // Price after discounts
  discount_on_unit_quantity?: number;
  discount_on_total_quantity?: number;
  quantity: number;
  tax_details: {
    tax_rate: number;
    tax_amount: number;
    applied_state: string;
  };
  is_taxable?: boolean;
  package_dimensions?: PackageDimensions | null;
  image_url?: string;
  images: any[];
  amount: number;
  tax_rule: TaxRule;
  tax_group: TaxGroup;
  customizations?: {
    template_name: string;
    fields: {
      field_name: string;
      field_type: "text" | "file";
      required: boolean;
      value?: string;
    }[];
  },
  design_id?: string;
  design_name?: string;
  preview_url?: string;
  item_tax_info: MaybeLineTax;
  storage_location: {
    bin_id: string;
    bin: string;
    rack_id: string;
    rack: string;
    warehouse_id: string;
    warehouse: string;
    zone_id: string;
    zone: string;
  }
}

// Address form values for billing/shipping address inputs
interface AddressFormValues {
  label_of_address: string;
  firstName: string;
  lastName: string;
  company: string;
  addressLine1: string;
  addressLine2: string;
  county: string;
  state: string;
  zipcode: string;
  country: string;
  phone: string;
}

// Office agent option for select dropdowns
interface OfficeAgentOption {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

// Generic selected option type for dropdowns
type Selected = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  globalUserId?: string;
  membershipId?: string;
  organizationId?: string;
}

// Form initial values for Formik - all form fields with default values
type InitialValues = {
  userType: string;
  userFirstName: string;
  userLastName: string;
  userEmail: string;
  userPhone: string;
  teamMemberId: string;
  teamMemberName: string;
  officeAgentId: string;
  officeId: string;
  office: string;
  agent: string;
  agent_id: string;
  label_of_address: string;
  firstName: string;
  lastName: string;
  company: string;
  addressLine1: string;
  addressLine2: string;
  country: string;
  city: string;
  county: string;
  state: string;
  zipcode: string;
  phone: string;
  billingAddressId: string;
  shippingAddressId: string;
  shippingMethod: string; // "free" | etc
  isSameAsBilling: boolean;
  notes: string;
  po_number: string;
  partial_amount: number;
};

// Agent option for select dropdowns
type AgentOption = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  organizationId?: string;
};

// UPS shipping option UI representation
type UpsUIOption = {
  id: string;
  code: string;        // UPS service code
  service: string;     // Display name (e.g., "UPS Ground")
  days: string;        // Delivery estimate (e.g., "3 business days")
  cost: number;        // Shipping cost
  subtitle: string;    // Additional info (e.g., "Guaranteed delivery")
};

// Team member option for select dropdowns (with office associations)
type BrokerageTeamMemberOption = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  offices: any[]; // Array of office objects from API
};

// Shipping payload types from child components
type ShipSelectedIds = {
  manual_shipping_id: string | null;
  ups_service_code: string | null;
  integration_id: string | null;
  shipper_address_id: string | null;
};

// Shipping payload structure from ShippingOptions component
type ShipPayload = {
  shipping_option?: Array<{
    shipping__id?: string;
    id?: string;
    shipping_amount?: number;
    shipping_tax_percentage?: number;
    shipping_taxable?: boolean;
    shipping_delivarydate?: string;     // Note: keeping both spellings for compatibility
    shipping_tax_amount?: number;
  }> | null;
  isUps?: boolean;
  isManual?: boolean;
  selected_ids?: ShipSelectedIds;
  ship_to?: any;
  packages?: any[];
};

function toCheckoutChargeShipPayload(
  payload: ShipPayload | { shipping_option?: unknown[] | null } | null | undefined
): { shipping_option?: unknown[] } | null | undefined {
  if (payload == null) return payload;
  const rows = payload.shipping_option;
  return { shipping_option: rows == null ? undefined : rows };
}

// Special fee row structure
type FeeRow = {
  specialfee_id: string;
  specialfee_name?: string;
  specialfee_amount: number;
  specialfee_tax_amount: number;
  specialfee_tax_percentage?: number;
  specialfee_taxable?: boolean;
  required?: boolean;
};

// Special fee payload from SpecialFeePicker component
type FeePayload = {
  specialfee?: FeeRow[];
  selected_fee_ids?: string[];
  total_amount?: number;
  total_tax_amount?: number;
};

// Shipping option export type (used by other components)
export type ShippingOption = {
  shipping__id: string;
  shipping_name?: string;
  shipping_amount: number;
  shipping_tax_percentage: number;
  shipping_tax_amount: number;
  shipping_taxable: boolean;
  shipping_delivarydate?: string;
  shipping_delivery_time?: string;
};

// Special fee export type (used by other components)
export type SpecialFee = {
  specialfee_id: string;
  specialfee_name: string;
  specialfee_amount: number;
  specialfee_tax_percentage: number;
  specialfee_tax_amount: number;
  specialfee_taxable: boolean;
  required: boolean;
};

// ============================================================================
// HELPER FUNCTIONS & CONSTANTS
// ============================================================================
// Number conversion helper - safely converts any value to number (defaults to 0)
const n = (v: any) => Number(v || 0);

// Normalize shipping rows to consistent format [id, amount, tax_amount, taxable]
const normShip = (rows?: any[] | null) =>
  (rows ?? []).map((r: any) => [r.shipping_id ?? "", n(r.shipping_amount), n(r.shipping_tax_amount), r.shipping_taxable !== false]);

// Normalize special fee rows to consistent format [id, amount, tax_amount, taxable]
const normFee = (rows?: any[] | null) =>
  (rows ?? []).map((r: any) => [r.specialfee_id ?? "", n(r.specialfee_amount), n(r.specialfee_tax_amount), r.specialfee_taxable !== false]);

const resolveBrokerageCheckoutRecipientType = (
  licenseTypeValue?: string | null
): "agent" | "brokerage_team" => {
  const lt = String(licenseTypeValue || "").toUpperCase();
  if (lt === "AGENT" || lt === "BROKERAGE_AGENT") return "agent";
  return "brokerage_team";
};

const resolveBrokerageCheckoutBuyerRecipientType = (
  buyerType?: string | null
): "agent" | "brokerage_team" => {
  const bt = String(buyerType || "").trim().toLowerCase();
  if (bt === "agent") return "agent";
  return "brokerage_team";
};

const resolveBrokerageCreatorPlacedRecipientType = (
  licenseTypeValue?: string | null
): "brokerage_admin" | "brokerage_team_member" => {
  const lt = String(licenseTypeValue || "BROKERAGE_FIRM").toUpperCase();
  return lt === "BROKERAGE_FIRM" ? "brokerage_admin" : "brokerage_team_member";
};

const resolveBrokerageEndUserPlacedRecipientType = (context: {
  selectedOfficeAgent?: { value?: string } | null;
  selectedAgent?: { value?: string } | null;
  selectedTeamMember?: { value?: string } | null;
  orderData?: {
    agent_id?: string;
    team_member_id?: string;
    brokerage_firm_id?: string;
    inventory_source?: string;
  } | null;
}): "agent" | "brokerage_admin" | "brokerage_team_member" => {
  if (context.selectedOfficeAgent?.value || context.selectedAgent?.value) return "agent";
  if (context.selectedTeamMember?.value) return "brokerage_team_member";
  const od = context.orderData;
  if (od?.agent_id) return "agent";
  if (od?.inventory_source === "office" && od?.team_member_id) return "brokerage_team_member";
  if (od?.brokerage_firm_id) return "brokerage_admin";
  return "brokerage_admin";
};

// Custom dropdown indicator component for react-select
const DropdownIndicator = (props: any) => {
  return (
    <div {...props.innerProps} className="d-flex align-items-center justify-content-center">
      <i className="ri-arrow-down-s-line fs-5 text-muted"></i>
    </div>
  );
};

// Custom styles for react-select components
const customSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(37, 99, 235, 0.25)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#2563eb' : '#9ca3af',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    padding: '8px',
  }),
}

// Product totals calculation type
type ProductTotals = { subTotal: number; discount: number; productTax: number };

// Google Maps API configuration
const libraries: Array<"places"> = ["places"];
const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

// Constants for AsyncPaginate (pagination size for async dropdowns)
const PAGE_SIZE = 10;

// Table column helper setup
type CartItemRow = CartItem;
const ch = createColumnHelper<CartItemRow>();

// ============================================================================
// MAIN COMPONENT: RealtorOrderCheckout
// ============================================================================
// This component handles the order checkout process for realtors.
// It manages product selection, addresses, shipping, taxes, discounts, and payment.
const RealtorOrderCheckout = () => {
  // ========================================================================
  // ROUTING & PARAMETERS
  // ========================================================================
  const { signageId } = useParams<{ signageId: string }>();
  const { productId } = useParams<{ productId: string }>();

  // ========================================================================
  // AUTHENTICATION & USER INFO
  // ========================================================================
  const token = getAuthCookie("sessionToken");
  const organizationId = getAuthCookie("organizationId");
  const membershipId = getAuthCookie("membershipId");
  const globalUserId = getAuthCookie("globalUserId");
  const userName = getAuthCookie("userName");
  const licenseType = getAuthCookie("licenseType");
  const supplierId = localStorage.getItem("supplierId") || "";
  const cartId = localStorage.getItem("cart_id");
  // Legacy cookie removed â€” keep alias only for remaining non-auth call sites.
  const userId = membershipId || "";

  // ========================================================================
  // UI STATE - Loading & Errors
  // ========================================================================
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [activeTab, setActiveTab] = useState("pills-bill-info-tab");
  const [tabCount, setTabCount] = useState(1);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showOrderSummary, setShowOrderSummary] = useState(true);
  const [showTaxDetails, setShowTaxDetails] = useState(false);
  const pendingInstructionFilesRef = useRef<{ id: string; file: File }[]>([]);
  const handlePendingInstructionFilesChange = useCallback((items: { id: string; file: File }[]) => {
    pendingInstructionFilesRef.current = items;
  }, []);
  const [pickerOpen, setPickerOpen] = useState(false);

  // ========================================================================
  // PRODUCT & CART STATE
  // ========================================================================
  const [product, setProduct] = useState<Product>();
  const [productobj, setProductObj] = useState<ProductObj>();
  const [productImages, setProductImages] = useState<string[]>([]);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartHeader, setCartHeader] = useState<any>(null); // Holds discount/subtotal/total/charges from cart GET

  // ========================================================================
  // BUYER & ORGANIZATION SELECTION STATE
  // ========================================================================
  // Team member selection
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState<any | null>(null);
  // Parent brokerage (for teammember login)
  const [parentBrokerageId, setParentBrokerageId] = useState<string | null>(null);
  const [parentBrokerageName, setParentBrokerageName] = useState<string | null>(null);
  const [orgname, setOrgname] = useState<string | null>(null);
  // Office and agent selection
  const [offices, setOffices] = useState<Office[]>([]);
  const [officeAgents, setOfficeAgents] = useState<Agent[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedOffice, setSelectedOffice] = useState<Selected | null>(null);
  const [selectedOfficeAgent, setSelectedOfficeAgent] = useState<Selected | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Selected | null>(null);

  // ========================================================================
  // ADDRESS STATE
  // ========================================================================
  const [addressObjects, setAddressObjects] = useState<any[]>([]);
  const [userInfo, setUserInfo] = useState<License>();

  // ========================================================================
  // ORDER TOTALS & FINANCIAL STATE
  // ========================================================================
  const [subTotal, setSubTotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [selectedTaxRate, setSelectedTaxRate] = useState(0);
  const [partialAmount, setPartialAmount] = useState<number>(0);
  const [enableNet30, setEnableNet30] = useState(false);

  // ========================================================================
  // SHIPPING STATE
  // ========================================================================
  const [shippingCosts, setShippingCosts] = useState<ShippingCost[]>([]);
  const [selectedShippingOption, setSelectedShippingOption] = useState<string>("");
  const [shippingOptionError, setShippingOptionError] = useState(false);
  const [isLoadingUps, setIsLoadingUps] = useState(false);
  const [upsOptions, setUpsOptions] = useState<UpsUIOption[]>([]);
  const [liveShipping, setLiveShipping] = useState(0);
  const [shipPayload, setShipPayload] = React.useState<ShipPayload | null>(null);

  // ========================================================================
  // TAX STATE
  // ========================================================================
  const [productTaxMap, setProductTaxMap] = useState<Record<string, number>>({});
  const [taxBreakdown, setTaxBreakdown] = useState({ product: 0, shipping: 0, specialFee: 0 });
  const [liveSpecialFee, setLiveSpecialFee] = useState(0);
  const [useLiveTotals, setUseLiveTotals] = useState(true);

  // ========================================================================
  // SPECIAL FEES STATE
  // ========================================================================
  const [feePayload, setFeePayload] = React.useState<FeePayload | null>(null);

  // ========================================================================
  // COUPON & DISCOUNT STATE
  // ========================================================================
  const [perItemCouponMap, setPerItemCouponMap] = React.useState<Record<string, number>>({});
  const [productTotals, setProductTotals] = React.useState<ProductTotals>({
    subTotal: 0,
    discount: 0,
    productTax: 0,
  });

  // ========================================================================
  // PAYMENT STATE
  // ========================================================================
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("");
  const [clientSecret, setClientSecret] = useState<string | "">();
  const [canProceedToPayment, setCanProceedToPayment] = useState(false);
  const [showPaymentSection, setShowPaymentSection] = useState(false);
  const [checkoutPaymentOpen, setCheckoutPaymentOpen] = useState(false);
  const [checkoutPaymentOrder, setCheckoutPaymentOrder] = useState<Record<string, unknown> | null>(null);
  const dispatch = useAppDispatch();
  const { fetchCart, clearCartItems } = useCart();

  // ========================================================================
  // BUSY STATE TRACKING
  // ========================================================================
  // Centralized busy state tracking for various operations
  const [busyStates, setBusyStates] = useState({
    shipping: false,
    specialFee: false,
    productTax: false,
    mainForm: false,
  });

  // Compute if ANY operation is busy (for disabling UI during operations)
  const isAnythingBusy = React.useMemo(
    () => loading || Object.values(busyStates).some(b => b),
    [loading, busyStates]
  );

  // ========================================================================
  // REDUX & CONTEXT
  // ========================================================================
  // refs to child widgets
  const shipRef = React.useRef<ShippingOptionsV2Handle>(null);
  const feeRef = React.useRef<SpecialFeePickerV2Handle>(null);

  const lastResolvedSalesRef = React.useRef<ResolvedSalesOrder | null>(null);
  const isRecomputingRef = React.useRef(false);
  const [isRecomputingSales, setIsRecomputingSales] = React.useState(false);
  const [couponInput, setCouponInput] = React.useState("");
  const [couponResolveError, setCouponResolveError] = React.useState("");
  const [liveDiscountDisplay, setLiveDiscountDisplay] = React.useState({ code: "", amount: 0 });
  const [resolvedTotalsTick, setResolvedTotalsTick] = React.useState(0);
  const userClearedCouponRef = React.useRef(false);
  const recomputeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const shipPayloadRef = React.useRef<ShipPayload | null>(null);
  const feePayloadRef = React.useRef<FeePayload | null>(null);
  const specialFeeCatalogRef = React.useRef<unknown[]>([]);
  const recomputeSalesRef = React.useRef<
    ((override?: {
      discountCode?: string;
      force?: boolean;
      feePayload?: FeePayload | null;
      shipPayload?: ShipPayload | null;
    }) => Promise<boolean>) | null
  >(null);
  const formikValuesRef = React.useRef<Record<string, unknown>>({});

  const handleShipPayloadChange = React.useCallback((p: ShipPayload) => {
    shipPayloadRef.current = p;
    setShipPayload(prev => {
      const prevKey = JSON.stringify({
        rows: (prev?.shipping_option ?? []).map(r => [r.shipping__id ?? r.id, r.shipping_amount, r.shipping_tax_amount]),
        ids: prev?.selected_ids, ups: !!prev?.isUps, man: !!prev?.isManual
      });
      const nextKey = JSON.stringify({
        rows: (p?.shipping_option ?? []).map(r => [r.shipping__id ?? r.id, r.shipping_amount, r.shipping_tax_amount]),
        ids: p?.selected_ids, ups: !!p?.isUps, man: !!p?.isManual
      });
      return (prevKey === nextKey) ? prev : p;
    });
    if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
    recomputeTimerRef.current = setTimeout(() => {
      void recomputeSalesRef.current?.({ shipPayload: p });
    }, 300);
  }, []);

  const [activeTaxType, setActiveTaxType] = React.useState<"manual" | "taxcloud" | "geera">("geera");
  const [getstatusTaxType, setGetStatusTaxType] = React.useState("");
  const [taxError, setTaxError] = React.useState<string>("");
  const asArray = <T,>(val: T | T[] | null | undefined): T[] =>
    Array.isArray(val) ? val : (val ? [val] : []);
  const money = (v: any) =>
    Number.isFinite(Number(v)) ? `$${Number(v).toFixed(2)}` : "TBD";
  const shippingOptions = shippingCosts?.map((cost) => ({
    value: cost._id,
    label: cost.name,
    description: cost.description,
    cost: cost.is_free ? 0 : (cost.cost ?? 0),
    is_free: cost.is_free,
    id: cost._id,
    is_taxable: cost.is_taxable,
    taxable: cost.taxable
  }));
  const location = useLocation();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const navigationOrderId = String(location.state?.orderId ?? "").trim() || null;
  const [linkedOrderIdFromCart, setLinkedOrderIdFromCart] = useState<string | null>(null);
  const [cartGlobalUserId, setCartGlobalUserId] = useState("");
  const [cartBuyerOrgId, setCartBuyerOrgId] = useState("");
  const [cartBuyerMembershipId, setCartBuyerMembershipId] = useState("");
  const [linkedOrderNumberFromCart, setLinkedOrderNumberFromCart] = useState<string | null>(null);
  // Resume when cart carries order_id, or when opened from order details with navigation state.
  const resumeOrderId = linkedOrderIdFromCart || navigationOrderId || "";
  const isEdit = Boolean(resumeOrderId);
  const addProductRow = () => setPickerOpen(true);
  const closePicker = () => setPickerOpen(false);

  // Helper function for AsyncPaginate

  const asNum = (v: any) => Number(v || 0);
  const round2 = (n: number) => Math.round(asNum(n) * 100) / 100;
  const keyForRow = (r: { productId: string; variantId: string }) => `${r.productId}:${r.variantId}`;

  const normalizeId = (row: any) => String(row?.id ?? row?._id ?? "");

  const mapTeamMemberToOption = (row: any) =>
    mapFoundationMembershipRowToOption(row, {
      officeId: selectedOffice?.value,
    });

  const mapAgentToOption = (row: any) => mapFoundationMembershipRowToOption(row);

  // Get brokerage ID based on login type
  const brokerageOrgId = resolveBrokerageOrganizationId();

   useEffect(() => {
      const displayName = (userName || "").replace(/^"|"$/g, "").trim();
      const nameParts = displayName.split(/\s+/).filter(Boolean);
      setUserInfo({
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        email: getAuthCookie("email") || "",
        firmDetails: {
          companyName: (isTeammember && orgname ? orgname : displayName) || "",
        },
      } as License);
    }, [userName, orgname, isTeammember]);

  // ===== Office loader using Select2 endpoint (NEW FLOW) =====
  const loadOfficeOptions: LoadOptions<Selected, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      // Guard: without brokerage ID, do nothing
      if (!brokerageOrgId) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      try {
        const q = (inputValue || "").trim();

        const json = await fetchOfficeSelect(token, brokerageOrgId, {
          page,
          limit: PAGE_SIZE,
          search: q,
        });

        const list = json?.results || [];

        const options: Selected[] = list.map((office: any) => ({
          value: office.id || office._id,
          label: office.officeName || "---",
          firstName: office.officeName || "",
          lastName: "",
          email: "",
          phone: office.officeAddress?.[0]?.phone || "",
        }));

        const hasMore = json?.pagination?.more ?? false;

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        safeConsole.error("Failed to load office options:", err);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [brokerageOrgId, token]
  );

  // ===== Team Member Loader using Select2 endpoint (based on office - NEW FLOW) =====
  const loadTeamMemberOptions: LoadOptions<BrokerageTeamMemberOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      // HARD GUARD: without office, do nothing
      const officeId = selectedOffice?.value;
      if (!officeId) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      try {
        const q = (inputValue || "").trim();

        const json = await fetchOfficeTeamMembersSelect(token, officeId, {
          page,
          limit: PAGE_SIZE,
          search: q,
        });

        const list = json?.results || [];

        const options: BrokerageTeamMemberOption[] = list.map((u: any) =>
          mapFoundationMembershipRowToOption(u, { officeId })
        );

        const hasMore = json?.pagination?.more ?? false;

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (error) {
        safeConsole.error("Error loading team member options:", error);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [selectedOffice?.value, token]
  );

  // ===== Office Agent loader using Select2 endpoint (based on office - NEW FLOW) =====
  const loadOfficeAgentOptions: LoadOptions<AgentOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      // HARD GUARD: without office, do nothing
      const officeId = selectedOffice?.value;
      if (!officeId) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      try {
        const q = (inputValue || "").trim();

        const json = await fetchAgentSelect(token, officeId, {
          page,
          limit: PAGE_SIZE,
          search: q,
        });

        const list = json?.results || [];

        const options: AgentOption[] = list.map((u: any) =>
          mapFoundationMembershipRowToOption(u)
        );

        const hasMore = json?.pagination?.more ?? false;

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (error) {
        safeConsole.error("Failed to load office agent options:", error);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [selectedOffice?.value, token]
  );

  // ===== Agent Loader (Select2 style - for direct agent selection) =====
  const loadAgentOptions: LoadOptions<AgentOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      // safeConsole.log("AgentLoader firing with:", { page, inputValue });

      try {
        // Build URL with pagination and search
        const params = new URLSearchParams({
          licenseType: "BROKERAGE_AGENT",
          brokerageFirmID: resolveBrokerageFirmIdForAgentSearch(organizationId) || "",
          page: String(page),
          limit: String(PAGE_SIZE)
        });
        const search = (inputValue || "").trim();
        if (search) params.set("search", search);

        const responseData = await searchUsers(token, params.toString());
        // safeConsole.log('Agent API response:', { page, search, responseData });

        // Handle different response formats
        const results = Array.isArray(responseData) ? responseData : responseData.results || [];
        const hasMore = Array.isArray(responseData)
          ? results.length === PAGE_SIZE
          : responseData.pagination?.more || false;

        // Map to react-select options using normalized ID
        const options: AgentOption[] = results.map(mapAgentToOption);

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (error) {
        safeConsole.error("Error loading agent options:", error);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [organizationId, token]
  );


  const [address, setAddress] = useState("");
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });
  const navigate = useNavigate();
  const safeNavigate = createSafeNavigate(navigate);
  const [orderId, setOrderId] = useState("");
  const [order, setOrder] = useState<Order | null>(null);
  const [editOrder, setEditOrder] = React.useState<any>(null);
  const [cartSellerOrgId, setCartSellerOrgId] = useState("");
  const [cartSellerMembershipId, setCartSellerMembershipId] = useState("");
  const [cartSellerGlobalUserId, setCartSellerGlobalUserId] = useState("");
  const sellerOrganizationId = React.useMemo(
    () =>
      resolveCheckoutVendorOrganizationId({
        cartSellerOrgId,
        order: editOrder || order,
        supplierIdFallback: supplierId,
      }),
    [cartSellerOrgId, editOrder, order, supplierId]
  );
  const [selectedBillingAddress, setSelectedBillingAddress] =
    useState<Addresses | null>(null);

  const [selectedShippingAddress, setSelectedShippingAddress] =
    useState<Addresses | null>(null);
  const [selectedOfficeBillingAddress, setSelectedOfficeBillingAddress] =
    useState<Addresses | null>(null);

  const [selectedOfficeShippingAddress, setSelectedOfficeShippingAddress] =
    useState<Addresses | null>(null);
    // safeConsole.log("selectedOfficeShippingAddress", selectedOfficeShippingAddress);

  useEffect(() => {
    async function fetchOrderMetadata() {
      try {
        setLoading(true);
        const data = await fetchOrderById(token, resumeOrderId);
        setEditOrder(data);
        setOrder(data);
        setOrderId(resumeOrderId);

        if (data?.billing_address) {
          setSelectedBillingAddress(data.billing_address);
          setSelectedOfficeBillingAddress(data.billing_address);
        }
        const sameAsBilling =
          data?.billing_address?._id &&
          data?.shipping_address?._id &&
          data.billing_address._id === data.shipping_address._id;
        if (sameAsBilling && data?.billing_address) {
          setSelectedShippingAddress(data.billing_address);
          setSelectedOfficeShippingAddress(data.billing_address);
        } else if (data?.shipping_address) {
          setSelectedShippingAddress(data.shipping_address);
          setSelectedOfficeShippingAddress(data.shipping_address);
        }

        // Line items come from cart when cart_id exists; only fall back to order items without a cart.
        if (!cartId) {
          setCartItems(Array.isArray(data?.items) ? data.items : []);
        }

        if (data?.buyer_type === "teammember") {
          // âœ… Set selected office
          if (data?.office?._id) {
            // safeConsole.log("Inside the Brokerage Firm and Office condition");
            setSelectedOffice({
              value: data.office._id,
              label: data.office.officeName,
              firstName: data.office.officeName,
              lastName: data.office.officeName,
              email: "", // fallback or set as per API
              phone: data.office.officeAddress?.phone || "",
            });
          }

          // âœ… Set selected office agent
          if (data?.agent_id) {
            // safeConsole.log("Inside the Brokerage Firm and Office Agent condition");
            setSelectedOfficeAgent({
              value: data.agent_id,
              label: `${data.customer_first_name} ${data.customer_last_name}`,
              firstName: data.customer_first_name,
              lastName: data.customer_last_name,
              email: data.customer_email || "",
              phone: data.customer_phone || "",
            });
          }
        }
        else {
          if (data?.agent_id) {
            // safeConsole.log("INside the Direct Agent condition");
            setSelectedAgent({
              value: data.agent_id,
              label: `${data.customer_first_name} ${data.customer_last_name}`,
              firstName: data.customer_first_name,
              lastName: data.customer_last_name,
              email: data.customer_email || "",
              phone: data.customer_phone || "",
            });
          }
        }

        // ðŸ‘‰ key line: map response to form initial values
        setInitialValues(mapOrderToForm(data));

        if (data?.discount_code) {
          const code = String(data.discount_code).trim();
          setCouponInput(code);
          setLiveDiscountDisplay({
            code,
            amount: Number(data.discount_amount || 0),
          });
        }

        // after hydrating rows + maps from order response
        hydratedRef.current = true;
        setHydrated(true);

      } catch (e) {
        await handleApiError(e, {
          module: "sales_order",
          action: "realtor_order_checkout_fetch_order_data",
          userId: userId || undefined,
          url: `${SALES_ORDER_API_BASE}${salesOrderUrls.order(resumeOrderId)}`,
          method: "GET",
          additionalInfo: { orderId: resumeOrderId }
        }, token);
        safeConsole.error("Error fetching order data:", e);
      } finally {
        setLoading(false);
      }
    }

    if (isEdit && token) fetchOrderMetadata();
    else if (!navigationOrderId && !linkedOrderIdFromCart) setInitialValues(emptyInitialValues);
  }, [isEdit, resumeOrderId, token, cartId, navigationOrderId, linkedOrderIdFromCart]);

  const DEFAULT_TEN_ZEROS = "0000000000";
  const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
    !v || v.trim() === "" || v === def ? def : v;
  const emptyInitialValues: InitialValues = {
    userType: "",
    userFirstName: "",
    userLastName: "",
    userEmail: "",
    userPhone: "",
    officeAgentId: "",
    teamMemberId: "",
    teamMemberName: "",
    officeId: "",
    office: "",
    agent: "",
    agent_id: "",
    label_of_address: "",
    firstName: "",
    lastName: "",
    company: "",
    addressLine1: "",
    addressLine2: "",
    country: "",
    county: "",
    city: "",
    state: "",
    zipcode: "",
    phone: DEFAULT_TEN_ZEROS || "",
    billingAddressId: "",
    shippingAddressId: "",
    shippingMethod: "free",
    isSameAsBilling: false,
    notes: "",
    po_number: "",
    partial_amount: 0,
  };
  const [initialValues, setInitialValues] = React.useState<InitialValues>(emptyInitialValues);
  function mapOrderToForm(data: any): InitialValues {
    if (data?.signage_id) {
      localStorage.setItem("supplierId", data.signage_id);
    }
    if (data?.seller_org_id) {
      persistSupplierOrganizationId(data.seller_org_id);
    }
    const billing = data?.billing_address || {};
    const shipping = data?.shipping_address || {};

    const sameAsBilling = billing?._id && shipping?._id ? billing._id === shipping._id : true;

    return {
      userType: data?.buyer_type || "",
      userFirstName: data?.customer_first_name || "",
      userLastName: data?.customer_last_name || "",
      userEmail: data?.customer_email || "",
      userPhone: data?.customer_phone || "",

      // set these if you have them; otherwise keep as blank
      teamMemberId: data?.team_member_id || "",
      teamMemberName: data?.team_member_name || "",
      officeId: data?.office?._id,
      office: data?.brokerage_firm_name || "",
      officeAgentId: data?.agent_id || "",
      agent: data?.customer_first_name ? `${data.customer_first_name} ${data.customer_last_name || ""}`.trim() : "",
      agent_id: data?.agent_id || "",

      // If you want to show label on formâ€™s address block too
      label_of_address: billing?.label || "",

      // If you want â€œmain addressâ€ fields to reflect billing by default
      firstName: data?.customer_first_name || "",
      lastName: data?.customer_last_name || "",
      company: data?.brokerage_firm_name || "",
      addressLine1: billing?.address_details?.addressLine1 || "",
      addressLine2: billing?.address_details?.addressLine2 || "",
      country: billing?.address_details?.country || "",
      county: "", // not in API
      city: billing?.address_details?.city || "",
      state: billing?.address_details?.state || "",
      zipcode: billing?.address_details?.zip || "",
      phone: billing?.address_details?.phone || "",

      billingAddressId: billing?._id || "",
      shippingAddressId: shipping?._id || "",

      // derive a usable shippingMethod string (fallback to "free")
      shippingMethod:
        data?.shipping_method?.service_name ||
        (data?.manual_shipping_method?.is_free ? "free" : (data?.manual_shipping_method?.name || "free")),

      isSameAsBilling: sameAsBilling,

      // put notes here if you store them (not in your sample)
      notes: data?.notes || "",
      po_number: String(data?.po_number ?? data?.poNumber ?? "").trim(),
      partial_amount: 0,
    };
  }
  const handleAddFromPicker = (payload: any) => {
    // safeConsole.log("Picker payload:", payload);

    // accept only real, positive numbers for all 4 fields
    const validDims = (d: any) =>
      d &&
      Number.isFinite(d.length) && d.length > 0 &&
      Number.isFinite(d.width) && d.width > 0 &&
      Number.isFinite(d.height) && d.height > 0 &&
      Number.isFinite(d.weight) && d.weight > 0;

    // If popup sent a fully-formed payload (as in your safeConsole), prefer that.
    const hasPreparedPayload =
      payload &&
      payload.product_id &&
      payload.variant_id &&
      payload.customizations &&
      Array.isArray(payload.customizations.fields);

    // Prefer variant â†’ product â†’ payload-level package_dimensions (no defaults, no conversion)
    const variantFromPayload =
      payload?.product?.variants?.find((vv: any) => vv.variant_id === payload?.variant_id) ||
      payload?.product?.variants?.[0];

    const rawDims =
      variantFromPayload?.package_dimensions ??
      payload?.product?.package_dimensions ??
      payload?.package_dimensions;

    // Helper to derive storage location if not provided
    const deriveStorageFromProduct = (productobj: any) => {
      const p = productobj?.product;
      const v =
        (Array.isArray(p?.variants) && p.variants.find((vv: any) => vv.variant_id === productobj?.variant_id)) ||
        (Array.isArray(p?.variants) && p.variants[0]) ||
        undefined;

      const invList: any[] =
        (Array.isArray(v?.inventory) && v!.inventory) ||
        (Array.isArray(p?.inventory) && p!.inventory) ||
        [];

      const invEntry: any = invList.find((e) => (e?.available ?? 0) > 0) || invList[0] || null;

      return invEntry
        ? {
          bin_id: invEntry.bin_id || "",
          bin: invEntry.bin_name || "",
          rack_id: invEntry.rack_id || "",
          rack: invEntry.rack_name || "",
          warehouse_id: invEntry.warehouse_id || "",
          warehouse: invEntry.warehouse_name || "",
          zone_id: invEntry.zone_id || "",
          zone: invEntry.zone_name || "",
        }
        : {
          bin_id: "",
          bin: "",
          rack_id: "",
          rack: "",
          warehouse_id: "",
          warehouse: "",
          zone_id: "",
          zone: "",
        };
    };

    // Build the source object weâ€™ll map to CartItem
    const src = hasPreparedPayload
      ? payload
      : {
        // fallback: derive from product object (no customization values in this path)
        product_id: payload?.product_id,
        variant_id: payload?.variant_id || payload?.product?.variants?.[0]?.variant_id || "",
        name: payload?.name || payload?.product?.variants?.[0]?.variant_name || payload?.product?.name || "",
        sku: payload?.sku || payload?.product?.variants?.[0]?.sku || payload?.product?.sku || "",
        unit_purchase_price:
          payload?.unit_purchase_price ??
          payload?.product?.variants?.[0]?.purchase_price ??
          payload?.product?.purchase_price ??
          0,
        unit_retail_price:
          payload?.unit_retail_price ??
          payload?.product?.variants?.[0]?.retail_price ??
          payload?.product?.retail_price ??
          0,
        quantity: 1,
        tax_details: {
          tax_rate: payload?.product?.tax_rule?.taxRate ?? 0,
          tax_amount: 0,
          applied_state: payload?.product?.tax_rule?.state ?? "",
        },
        tax_rule: payload?.product?.tax_rule || {},
        tax_group: payload?.product?.tax_group || {},
        image_url:
          (Array.isArray(payload?.product?.images) && payload.product.images[0]?.url) ||
          payload?.product?.image?.url ||
          "",
        images: payload?.product?.images,
        storage_location:
          payload?.storage_location && payload.storage_location.warehouse_id
            ? payload.storage_location
            : deriveStorageFromProduct(payload),
        customizations: {
          template_name:
            payload?.customizations?.template_name ||
            payload?.product?.variants?.[0]?.variant_name ||
            payload?.product?.name ||
            "",
          fields:
            (payload?.customizations?.fields || []).map((f: any) => ({
              field_name: f.field_name,
              field_type: f.field_type,
              required: !!f.required,
              value: f.value ?? "", // nothing to wipe here
            })) || [],
        },
        amount: 0,
        // ðŸ‘‡ include dims ONLY if all fields are > 0; otherwise omit
        ...(validDims(rawDims)
          ? {
            package_dimensions: {
              length: rawDims.length,
              width: rawDims.width,
              height: rawDims.height,
              weight: rawDims.weight,
              ...(rawDims.weight_type ? { weight_type: rawDims.weight_type } : {}),
            },
          }
          : {}),
      };
    const duplicate = cartItems.some(
      (ci) => ci.product_id === src.product_id && ci.variant_id === src.variant_id
    );
    if (duplicate) {
      // toast.warning("This product is already added");
      return;
    }

    // Map -> CartItem (preserve customization values!)
    const item: CartItem = {
      product_id: src.product_id,
      variant_id: src.variant_id,
      product_name: src.product_name || src.name || "",
      storage_location: {
        bin_id: src.storage_location?.bin_id || "",
        bin: src.storage_location?.bin || "",
        rack_id: src.storage_location?.rack_id || "",
        rack: src.storage_location?.rack || "",
        warehouse_id: src.storage_location?.warehouse_id || "",
        warehouse: src.storage_location?.warehouse || "",
        zone_id: src.storage_location?.zone_id || "",
        zone: src.storage_location?.zone || "",
      },
      warehouse_id: src.storage_location?.warehouse_id || "",
      name: src.name || "",
      sku: src.sku || "",
      unit_purchase_price: Number(src.unit_purchase_price || 0),
      unit_retail_price: Number(src.unit_retail_price || 0),
      quantity: Number(src.quantity || 1),
      tax_details: {
        tax_rate: Number(src?.tax_details?.tax_rate || 0),
        tax_amount: Number(src?.tax_details?.tax_amount || 0),
        applied_state: src?.tax_details?.applied_state || "",
      },
      item_tax_info: [],
      image_url: src.image_url || "",
      images: src.images,
      amount:
        typeof src.amount === "number"
          ? src.amount
          : Number(src.quantity || 1) * Number(src.unit_retail_price || 0),
      tax_rule: src.tax_rule || ({} as TaxRule),
      tax_group: src.tax_group || ({} as TaxGroup),
      ...(src.package_dimensions ? { package_dimensions: src.package_dimensions } : {}), // only if valid
      customizations: src.customizations
        ? {
          template_name: src.customizations.template_name || "",
          fields: (src.customizations.fields || []).map((f: any) => ({
            field_name: f.field_name,
            field_type: f.field_type,
            required: !!f.required,
            value: f.value ?? "", // keep value
          })),
        }
        : { template_name: "", fields: [] },
    };
    const next = [...cartItems, item];
    setCartItems(next);
    setPickerOpen(false);
  };


  // ===== OLD FLOW FUNCTIONS REMOVED - now using AsyncPaginate with Select2 endpoints (NEW FLOW) =====

  // ===== NEW FLOW: Offices are now loaded via AsyncPaginate directly (office-first) =====
  // No need to load offices based on team member selection anymore

  const userOptions: { value: string; label: string }[] = [
    { value: "teammember", label: "Team Member" },
    { value: "agent", label: "Agent" },
  ];

  // ===== OLD FLOW: officeAgentOptions removed - now using AsyncPaginate with loadOfficeAgentOptions (NEW FLOW) =====

  const addressOptions =
    addressObjects !== null
      ? addressObjects?.map((address) => ({
        value: address._id,
        label: `${address?.label}: ${address?.address_details?.addressLine1}, ${address?.address_details?.city}, ${address?.address_details?.state}, ${address?.address_details?.zip ? address?.address_details?.zip : ''}`,
      }))
      : [];



  // Load cart: always use cart_items for the product table.
  // When cart.order_id is set, checkout resumes that draft order (metadata loaded separately).
  useEffect(() => {
    const fetchCartItems = async () => {
      try {
        setLoading(true);
        const data = await fetchCartById(token, cartId);
        const cartIds = readCartFoundationIds(data);
        setCartGlobalUserId(cartIds.buyerGlobalUserId);
        setCartBuyerOrgId(cartIds.buyerOrgId);
        setCartBuyerMembershipId(cartIds.buyerMembershipId);
        if (cartIds.sellerOrgId) {
          setCartSellerOrgId(cartIds.sellerOrgId);
          persistSupplierOrganizationId(cartIds.sellerOrgId);
          if (typeof localStorage !== "undefined") {
            localStorage.setItem("supplierId", cartIds.sellerOrgId);
          }
        }
        setCartSellerMembershipId(cartIds.sellerMembershipId);
        setCartSellerGlobalUserId(cartIds.sellerGlobalUserId);
        const linkedId = getResumeOrderIdFromCart(data);
        const linkedNumber = getResumeOrderNumberFromCart(data);

        setLinkedOrderIdFromCart(linkedId);
        setLinkedOrderNumberFromCart(linkedNumber);
        if (linkedId) setOrderId(linkedId);

        const lineItems = getLineItemsFromCart(data);
        setCartItems(lineItems);

        // Create mode only: summary/charges/discount seed from cart (resume uses order + live coupon resolve).
        if (!linkedId) {
          setCartHeader(buildCartHeaderFromCart(data));
          const hydratedCoupon = buildPerItemCouponMapFromCartItems(lineItems);
          setPerItemCouponMap((prev) => ({ ...hydratedCoupon, ...prev }));
          const header = buildCartHeaderFromCart(data);
          if (header.discount_code) {
            const code = String(header.discount_code).trim();
            setCouponInput(code);
            setLiveDiscountDisplay({
              code,
              amount: Number(header.discount_amount || 0),
            });
          }
        }

        hydratedRef.current = true;
        setHydrated(true);
      } catch (error) {
        await handleApiError(error, {
          module: "sales_order",
          action: "realtor_order_checkout_fetch_cart_items",
          userId: userId || undefined,
          url: `${SALES_ORDER_API_BASE}${salesOrderUrls.cart(cartId)}`,
          method: "GET",
          additionalInfo: { cartId }
        }, token);
        safeConsole.error("Error fetching cart items:", error);
      } finally {
        setLoading(false);
      }
    };

    if (cartId) {
      fetchCartItems();
    }
  }, [cartId, token]);

  // useEffect(() => {
  //   const checkCreditFacility = async () => {
  //     const net30GroupId = "67bc75781a32a989c4e6477c";
  //     try {
  //       const response = await fetch(
  //         `${process.env.REACT_APP_API_URL}/customer-groups/${net30GroupId}/users/${userId}/exists`,
  //         {
  //           method: "GET",
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             "Content-Type": "application/json",
  //           },
  //         }
  //       );
  //       if (!response.ok) {
  //         throw new Error(`Failed to fetch credit facility data: ${response.statusText}`);
  //       }
  //       const data = await response.json();
  //       if (data) {
  //         // safeConsole.log("Credit Facility Data:", data);
  //         if (data.exists) {
  //           setEnableNet30(true);
  //         }
  //       } else {
  //         safeConsole.error("Incomplete data", data);
  //       }
  //     } catch (error) {
  //       // await handleApiError(error, {
  //       //   module: "sales_order",
  //       //   action: "realtor_order_checkout_check_credit_facility",
  //       //   userId: userId || undefined,
  //       //   url: `${process.env.REACT_APP_API_URL}/customer-groups/${net30GroupId}/users/${userId}/exists`,
  //       //   method: "GET",
  //       //   additionalInfo: { net30GroupId, userId }
  //       // }, token);
  //       safeConsole.error("Error fetching credit facility data:", error);
  //     }
  //   };

  //   if (userId) {
  //     checkCreditFacility();
  //   }
  // }, [userId, token]);


  useEffect(() => {
    const selectedOption = shippingOptions?.find(opt => opt.value === selectedShippingOption);
    const shippingCost = selectedOption ? selectedOption.cost : 0;
    const newTotal = subTotal + tax + shippingCost;
    setTotalAmount(newTotal);
  }, [selectedShippingOption, shippingOptions, subTotal, tax]);

  // useEffect(() => {
  //   const checkCreditFacility = async () => {
  //     const net30GroupId = "67bc75781a32a989c4e6477c";
  //     try {
  //       const response = await fetch(
  //         `${process.env.REACT_APP_API_URL}/customer-groups/${net30GroupId}/users/${userId}/exists`,
  //         {
  //           method: "GET",
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             "Content-Type": "application/json",
  //           },
  //         }
  //       );
  //       if (!response.ok) {
  //         throw new Error("Failed to fetch data");
  //       }
  //       const data = await response.json();
  //       if (data) {
  //         // safeConsole.log("Credit Facility Data:", data);
  //         if (data.exists) {
  //           setEnableNet30(true);
  //         }
  //       } else {
  //         safeConsole.error("Incomplete data", data);
  //       }
  //     } catch (error) {
  //       safeConsole.error("Error fetching user data:", error);
  //     }
  //   };

  //   if (userId) {
  //     checkCreditFacility();
  //   }
  // }, [userId, token]);


  const [integrations, setIntegrations] = useState<PaymentGateway[]>([]);

  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        const orgId = sellerOrganizationId || resolveSupplierOrganizationId(supplierId, order);
        const data = await fetchPaymentGatewayBySupplier(token, orgId);
        setIntegrations(data);
      } catch (err) {
        // await handleApiError(err, {
        //   module: "sales_order",
        //   action: "realtor_order_checkout_fetch_integrations",
        //   userId: userId || undefined,
        //   url: `${process.env.REACT_APP_API_URL}/payments/gateway/created-by/${supplierId}`,
        //   method: "GET",
        //   additionalInfo: { supplierId }
        // }, token);
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchIntegrations();
  }, [supplierId, sellerOrganizationId, order, token]);

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);


  const handleQuantityChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 1;

    setCartItems((prevItems) =>
      prevItems.map((item, i) =>
        i === index ? { ...item, quantity: value } : item
      )
    );
  };


  useEffect(() => {
    const stripeInfo = integrations?.find((method) => method.gateway === "stripe");
    // safeConsole.log('Stripe info ', stripeInfo);
    if (stripeInfo && stripeInfo.publishable_key) {
      const promise = loadStripe(stripeInfo.publishable_key);
      setStripePromise(promise);
    }
  }, [integrations]);


  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      userFirstName: Yup.string()
        .min(2, "Name must be at least 2 characters long")
        .max(50, "Name cannot be longer than 50 characters")
        .required("Name is required"),
      userEmail: Yup.string().email("Invalid email").required("Email is required"),
      userPhone: phoneValidation,
      
      // âœ… officeId required ONLY if userType is teammember (NEW FLOW)
      officeId: Yup.string().when("userType", {
        is: "teammember",
        then: (schema) => schema.required("Office selection is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      // âœ… teamMemberId required ONLY if userType is teammember (NEW FLOW)
      teamMemberId: Yup.string().when("userType", {
        is: "teammember",
        then: (schema) => schema.required("Team member is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      // âœ… agent_id required ONLY if userType is agent
      agent_id: Yup.string().when("userType", {
        is: "agent",
        then: (schema) => schema.required("Agent selection is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      billingAddressId: Yup.string().required("Billing address is required"),
      shippingAddressId: Yup.string().when("isSameAsBilling", {
        is: false,
        then: (schema) => schema.required("Shipping address is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      setActiveTab("pills-finish-tab");
      setSubmitting(false);
    },
  });


  const checkProceedEligibility = (billingId?: string, shippingId?: string) => {
    const billing = addressObjects?.find((address) => address._id === billingId);
    const shipping = addressObjects?.find((address) => address._id === shippingId);

    if (formik.values.isSameAsBilling) {
      // âœ… If same as billing, just check billing
      setCanProceedToPayment(!!billing);
      // safeConsole.log("Can proceed to payment (same as billing):", !!billing);
    } else {
      // âœ… Only enable if BOTH billing & shipping exist
      setCanProceedToPayment(!!billing && !!shipping);
      // safeConsole.log("Can proceed to payment (different billing & shipping):", !!billing && !!shipping);
    }
  };
  useEffect(() => {
    checkProceedEligibility(formik.values.billingAddressId, formik.values.shippingAddressId);
  }, [
    formik.values.billingAddressId,
    formik.values.shippingAddressId,
    formik.values.isSameAsBilling   // âœ… include this dependency
  ]);

  // âœ… NEW FLOW: Set default user type to "teammember" without auto-selecting team member
  useEffect(() => {
    if (!isEdit && !formik.values.userType) {
      formik.setFieldValue("userType", "teammember");
    }
  }, [isEdit, formik.values.userType]);

  const uploadOrderDocumentsToOrder = useCallback(
    async (orderDocumentId: string, files: File[]) => {
      const id = String(orderDocumentId || "").trim();
      if (!token || !id || !files.length) return;
      const fd = new FormData();
      fd.append("id", id);
      fd.append("collection_name", "sales");
      files.forEach((f) => fd.append("files[]", f));

      await uploadDocumentsToOrder(token, fd);
    },
    [token]
  );


  // build per-item tax info from your productTaxMap
  function buildItemTaxInfoAtCreate(
    row: { productId: string; variantId: string; sku: string; unitPrice: number; quantity: number },
    productTaxMap: Record<string, number>,
    shippingAddress: any
  ) {
    const k = keyForRow(row);
    const lineTax = asNum(productTaxMap[k] || 0);           // total tax for this line
    const qty = Math.max(1, asNum(row.quantity || 1));
    const unitTax = qty > 0 ? lineTax / qty : 0;            // per-unit tax
    const base = asNum(row.unitPrice || 0) * qty;        // pre-tax line base
    const ratePct = base > 0 ? (lineTax / base) * 100 : 0;  // % for display
    const appliedState = String(
      shippingAddress?.address_details?.stateCode ||
      shippingAddress?.address_details?.state || ""
    );


    return {
      tax_percentage: round2(ratePct),
      unit_product_tax_amount: round2(unitTax),
      total_product_tax_amount: round2(lineTax),
      tax_details: {
        tax_rate: round2(ratePct),
        tax_amount: round2(lineTax),
        applied_state: appliedState,
      },
      taxable: lineTax > 0 || ratePct > 0,
    };
  }

  const shippingAddrForTax =
    selectedShippingAddress || selectedOfficeShippingAddress ||
    selectedBillingAddress || selectedOfficeBillingAddress || null;

  const getUnitRetailPriceForPayload = React.useCallback(
    (productId: string, variantId: string, originalUnitPrice: number) => {
      const item = (cartItems || []).find(
        (ci) => String(ci.product_id) === productId && String(ci.variant_id) === variantId
      );
      return Number(item?.unit_retail_price ?? originalUnitPrice ?? 0);
    },
    [cartItems]
  );

  const getChangedUnitPriceForPayload = React.useCallback(
    (productId: string, variantId: string, originalUnitPrice: number) => {
      const item = (cartItems || []).find(
        (ci) => String(ci.product_id) === productId && String(ci.variant_id) === variantId
      );
      return Number(
        item?.changed_unit_price ?? item?.unit_retail_price ?? originalUnitPrice ?? 0
      );
    },
    [cartItems]
  );

  const getCheckoutChargeSource = React.useCallback(
    () =>
      editOrder ||
      order ||
      (cartHeader ? { order_charges: cartHeader.order_charges } : null),
    [editOrder, order, cartHeader]
  );

  const readLiveShipFeePayloads = React.useCallback(() => {
    const shipFromChild = shipRef.current?.getPayload?.();
    const feeFromChild = feeRef.current?.getPayload?.();
    const activeShipPayload =
      (Array.isArray(shipFromChild?.shipping_option) && shipFromChild.shipping_option.length
        ? shipFromChild
        : null) ??
      shipPayloadRef.current ??
      shipPayload;
    const activeFeePayload =
      (Array.isArray(feeFromChild?.specialfee) && feeFromChild.specialfee.length
        ? feeFromChild
        : null) ??
      feePayloadRef.current ??
      feePayload;
    return { activeShipPayload, activeFeePayload };
  }, [shipPayload, feePayload]);

  const getLiveCheckoutChargeRows = React.useCallback(() => {
    const { activeShipPayload, activeFeePayload } = readLiveShipFeePayloads();
    return getCheckoutOrderChargeFallbacks({
      shipPayload: toCheckoutChargeShipPayload(activeShipPayload),
      feePayload: activeFeePayload,
      savedOrder: getCheckoutChargeSource(),
    });
  }, [readLiveShipFeePayloads, getCheckoutChargeSource]);

  const applyResolvedSalesToUi = React.useCallback(
    (resolved: ResolvedSalesOrder, requestedDiscountCode?: string) => {
      const sync = applySalesResolveResponse(resolved, {
        requestedDiscountCode,
        userClearedCoupon: userClearedCouponRef.current,
        existingFeeRows: feePayloadRef.current?.specialfee,
        existingShipRows: shipPayloadRef.current?.shipping_option,
        specialFeeCatalog: specialFeeCatalogRef.current,
        shippingCostCatalog: shippingCosts,
      });
      setProductTaxMap(sync.productTaxMap);
      setTaxBreakdown(sync.taxBreakdown);
      setProductTotals(sync.productTotals);
      if (sync.feePayloadPatch?.specialfee?.length) {
        const resolvedFees = sync.feePayloadPatch.specialfee;
        const nextFeePayload: FeePayload = {
          specialfee: resolvedFees as FeeRow[],
          selected_fee_ids: (resolvedFees as Array<{ specialfee_id?: string }>)
            .map((f) => String(f.specialfee_id ?? ""))
            .filter(Boolean),
          total_amount: sync.feePayloadPatch.total_amount,
          total_tax_amount: sync.feePayloadPatch.total_tax_amount,
        };
        feePayloadRef.current = nextFeePayload;
        setFeePayload(nextFeePayload);
      }
      if (sync.shipPayloadPatch?.shipping_option?.length) {
        const nextShip: ShipPayload = {
          ...(shipPayloadRef.current ?? shipPayload ?? {}),
          shipping_option: sync.shipPayloadPatch.shipping_option as ShipPayload["shipping_option"],
        };
        shipPayloadRef.current = nextShip;
        setShipPayload(nextShip);
      }
      setCartItems((prev) => mergeCheckoutCartFromResolved(prev, resolved));
      setPerItemCouponMap(perItemCouponMapFromResolved(resolved));
      setLiveDiscountDisplay(sync.discountDisplay);
      setResolvedTotalsTick((t) => t + 1);
    },
    [shipPayload, shippingCosts]
  );

  const recomputeSalesOrder = React.useCallback(
    async (override?: {
      discountCode?: string;
      force?: boolean;
      feePayload?: FeePayload | null;
      shipPayload?: ShipPayload | null;
    }) => {
      if (!token) return false;
      if (isRecomputingRef.current && !override?.force) return false;
      if (!supplierId || !(cartItems?.length)) return false;

      const buyer = pickRealtorCheckoutResolveBuyer({
        values: formikValuesRef.current as any,
        selectedOfficeAgent,
        selectedAgent,
        selectedOffice,
        selectedTeamMemberOption: selectedTeamMember
          ? {
              value: selectedTeamMember.membershipId || selectedTeamMember.value,
              membershipId: selectedTeamMember.membershipId || selectedTeamMember.value,
              globalUserId: selectedTeamMember.globalUserId,
              organizationId: selectedOffice?.value || selectedTeamMember.organizationId,
            }
          : null,
        offices,
        selectedBillingAddress,
        selectedShippingAddress,
        selectedOfficeBillingAddress,
        selectedOfficeShippingAddress,
        userId: userId || "",
        userName: userName || "",
        isTeammember,
        parentBrokerageId,
        brokerageOrganizationId: organizationId,
        orgname: orgname ?? undefined,
        userInfo,
      });
      if (!buyer.customerId) return false;

      const sellerIds = resolveCheckoutSellerFoundationIds({
        cartSellerOrgId,
        cartSellerMembershipId,
        cartSellerGlobalUserId,
        routeSupplierOrgId: sellerOrganizationId || supplierId,
        order: (isEdit ? editOrder : order) as {
          seller_org_id?: string;
          seller_membership_id?: string;
          seller_global_user_id?: string;
        } | null,
      });
      const sellerOrgId =
        sellerIds.organization_id || sellerOrganizationId || cartSellerOrgId || supplierId || "";
      if (!sellerOrgId) return false;

      const discountCode = getSalesResolveDiscountCode({
        override: override?.discountCode,
        userClearedCoupon: userClearedCouponRef.current,
        couponInput,
        liveDiscountCode: liveDiscountDisplay.code,
        savedDiscountCode: isEdit
          ? editOrder?.discount_code
          : cartHeader?.discount_code,
      });

      isRecomputingRef.current = true;
      setIsRecomputingSales(true);
      setCouponResolveError("");

      try {
        const { activeShipPayload, activeFeePayload } = readLiveShipFeePayloads();
        const overrideShip = override?.shipPayload ?? activeShipPayload;
        const overrideFee = override?.feePayload ?? activeFeePayload;
        const chargeRows = getCheckoutOrderChargeFallbacks({
          shipPayload: toCheckoutChargeShipPayload(overrideShip),
          feePayload: overrideFee,
          savedOrder: getCheckoutChargeSource(),
        });

        const payload = buildSalesResolvePayload({
          sellerOrgId,
          sellerMembershipId: sellerIds.membership_id || undefined,
          sellerGlobalUserId: sellerIds.global_user_id || undefined,
          cartBuyerOrgId,
          cartBuyerMembershipId,
          cartBuyerGlobalUserId: cartGlobalUserId,
          buyerContext: buyer,
          signageId: sellerOrgId,
          signageCompanyName: userName || "",
          bothAddressSame: !!formikValuesRef.current.isSameAsBilling,
          discountCode,
          cartId: cartId ?? null,
          orderId: isEdit ? resumeOrderId : null,
          orderNumber: isEdit ? String(editOrder?.order_number || "").trim() || null : null,
          orderTaxStatus: editOrder?.order_tax_summary?.tax_status ?? "",
          selectedProducts: mapCheckoutCartToResolveCatalogRows(cartItems),
          shipPayload: overrideShip,
          feePayload: overrideFee,
          fallbackShippingRows: chargeRows.shippingRows,
          fallbackFeeRows: chargeRows.feeRows,
          shippingCosts,
          specialFeeCatalog: specialFeeCatalogRef.current,
          manualUnitPriceMap: {},
          getUnitRetailPriceForPayload,
          getChangedUnitPriceForPayload,
        });

        const resolved = await postSalesOrderResolve(token, {
          order_type: "order",
          action: "draft",
          payload,
        });

        lastResolvedSalesRef.current = resolved;
        applyResolvedSalesToUi(resolved, discountCode);
        return true;
      } catch (err: unknown) {
        const msg = String((err as Error)?.message ?? "Failed to recalculate order");
        setCouponResolveError(msg);
        showError(msg);
        return false;
      } finally {
        isRecomputingRef.current = false;
        setIsRecomputingSales(false);
      }
    },
    [
      token,
      supplierId,
      sellerOrganizationId,
      cartSellerOrgId,
      cartSellerMembershipId,
      cartSellerGlobalUserId,
      cartItems,
      cartId,
      cartBuyerOrgId,
      cartBuyerMembershipId,
      cartGlobalUserId,
      couponInput,
      liveDiscountDisplay.code,
      cartHeader?.discount_code,
      isEdit,
      resumeOrderId,
      editOrder,
      shipPayload,
      feePayload,
      shippingCosts,
      selectedOfficeAgent,
      selectedAgent,
      selectedOffice,
      selectedTeamMember,
      offices,
      organizationId,
      userId,
      userName,
      isTeammember,
      parentBrokerageId,
      orgname,
      userInfo,
      selectedBillingAddress,
      selectedShippingAddress,
      selectedOfficeBillingAddress,
      selectedOfficeShippingAddress,
      getUnitRetailPriceForPayload,
      getChangedUnitPriceForPayload,
      applyResolvedSalesToUi,
      readLiveShipFeePayloads,
      getCheckoutChargeSource,
    ]
  );

  recomputeSalesRef.current = recomputeSalesOrder;

  React.useEffect(() => {
    formikValuesRef.current = formik.values as Record<string, unknown>;
  }, [formik.values]);

  const shippingAddressSig = React.useMemo(() => {
    const isSame = !!formik.values.isSameAsBilling;
    const addr = isSame
      ? (selectedOfficeBillingAddress ?? selectedBillingAddress)
      : (selectedOfficeShippingAddress ?? selectedShippingAddress);
    const d = (addr as Addresses | null)?.address_details;
    if (!d) return "";
    return [d.zip, d.stateCode, d.city, d.addressLine1].filter(Boolean).join("|");
  }, [
    formik.values.isSameAsBilling,
    selectedBillingAddress,
    selectedShippingAddress,
    selectedOfficeBillingAddress,
    selectedOfficeShippingAddress,
  ]);

  React.useEffect(() => {
    if (!shippingAddressSig || !cartItems.length) return;
    if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
    recomputeTimerRef.current = setTimeout(() => {
      void recomputeSalesRef.current?.();
    }, 300);
    return () => {
      if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
    };
  }, [shippingAddressSig, cartItems.length]);

  async function createDraftOrder(): Promise<{ id: string; data: any }> {
    if (!token) {
      throw new Error("Missing session token");
    }

    const { shippingRows, feeRows } = getLiveCheckoutChargeRows();
    const { activeShipPayload, activeFeePayload } = readLiveShipFeePayloads();
    const resolveOk = await recomputeSalesRef.current?.({
      force: true,
      shipPayload: shippingRows.length
        ? { ...(activeShipPayload ?? {}), shipping_option: shippingRows as ShipPayload["shipping_option"] }
        : activeShipPayload,
      feePayload: feeRows.length
        ? { ...(activeFeePayload ?? {}), specialfee: feeRows as FeePayload["specialfee"] }
        : activeFeePayload,
    });
    if (!resolveOk || !lastResolvedSalesRef.current) {
      setLoading(false);
      throw new Error("Could not recalculate order before checkout. Please try again.");
    }

    const asNum = (v: any) => Number(v || 0);
    const round2 = (n: number) => Math.round(asNum(n) * 100) / 100;
    const keyFor = (ci: { product_id?: string; variant_id?: string }) =>
      `${String(ci.product_id || "")}:${String(ci.variant_id || "")}`;

    setLoading(true);

    // Declare payload outside try block so it's accessible in catch block
    let payload: any = null;

    try {
      // â€”â€”â€” addresses (default to GET values; reflect UI changes) â€”â€”â€”
      const finalBillingAddress =
        selectedOfficeBillingAddress ?? selectedBillingAddress ?? null;
      const finalShippingAddress =
        (formik.values.isSameAsBilling ? finalBillingAddress : null) ||
        selectedOfficeShippingAddress ||
        selectedShippingAddress ||
        finalBillingAddress;




      // â€”â€”â€” products (server defaults first, then apply live changes) â€”â€”â€”
      const keyForRow = (r: { productId: string; variantId: string }) => `${r.productId}:${r.variantId}`;

      // âœ… Build products with discount applied
      const perRowAlloc = perItemCouponMap || {};
      const products = (cartItems ?? []).map((item) => {
        const key = `${String(item.product_id)}:${String(item.variant_id)}`;
        const qty = Math.max(1, asNum(item.quantity));
        const unitBefore = asNum(item.unit_retail_price);
         const changedBefore = num(item.changed_unit_price);

        // âœ… Get current discount from map (scales with quantity for percentage discounts)
        const rowDiscTotal = round2(asNum(perItemCouponMap[key] || 0));
        safeConsole.log(rowDiscTotal,"jjjjjjjjjjjjjjjjjj")
        const perUnitDisc = round2(rowDiscTotal / qty);

        // âœ… Calculate prices with discount applied
        const lineBase = changedBefore * qty;                    // Subtotal before discount
        const lineAfter = Math.max(0, lineBase - rowDiscTotal); // After discount
        // const changedUnit = round2(lineAfter / qty);           // Discounted unit price
        const changedUnit = round2(changedBefore);
        safeConsole.log("Changed Unit Price:", changedUnit);
        const item_sub_total = round2(lineBase);              // Post-discount, pre-tax


        // // coupon allocation by composite key
        // const key = keyForRow({ productId: String(item.product_id), variantId: String(item.variant_id) });
        // const rowDiscTotal = round2(asNum((perRowAlloc || {})[key] || 0));

        // // line math (post-discount)
        // const lineBase = baseUnit * qty;                          // pre-discount
        // const lineAfter = Math.max(0, lineBase - rowDiscTotal);   // post-discount, pre-tax
        // const changedUnit = round2(lineAfter / qty);              // effective each after discount
        // const perUnitDisc = round2(rowDiscTotal / qty);
        // const item_sub_total = round2(changedUnit * qty);

        // Build jurisdiction details if you need them
        const taxInfoRaw = buildItemTaxInfoAtCreate(
          {
            productId: String(item.product_id),
            variantId: String(item.variant_id),
            sku: String(item.sku || ""),
            unitPrice: changedUnit,
            quantity: qty,
          },
          productTaxMap,
          shippingAddrForTax
        );

        // ðŸ”‘ Resolve live tax from TaxCloud map: SKU â†’ composite key
        const skuKey = String(item.sku || "");
        const liveRowTaxRaw =
          (skuKey && productTaxMap?.[skuKey] !== undefined ? productTaxMap[skuKey] : undefined) ??
          productTaxMap?.[key];
        const resolvedLineTax = Number(liveRowTaxRaw ?? NaN);

        // Final tax_amount: prefer live map, else fallback to computed/jurisdiction helper
        const tax_amount = Number.isFinite(resolvedLineTax)
          ? round2(resolvedLineTax)
          : round2(asNum((taxInfoRaw as any)?.total_product_tax_amount || 0));

        // Derive % and per-unit from the final tax_amount
        const tax_percentage = item_sub_total > 0 ? round2((tax_amount / item_sub_total) * 100) : 0;
        const unit_product_tax_amount = round2(qty ? tax_amount / qty : 0);

        const lineTaxable = readCheckoutCartLineTaxable(item);

        // Final tax block that the UI + payload will use
        const item_tax_info = {
          tax_percentage,
          unit_product_tax_amount,
          total_product_tax_amount: tax_amount,
          tax_details: (taxInfoRaw as any)?.tax_details || {},
          taxable: lineTaxable,
        };

        // Totals
        const total_price = round2(item_sub_total + tax_amount);

        const existingLineItemId = isEdit
          ? getCheckoutLineItemId(
              item as { _id?: unknown; id?: unknown; line_item_id?: unknown; product_id?: unknown; variant_id?: unknown },
              Array.isArray(editOrder?.items) ? editOrder.items : null
            )
          : "";

        return {
          ...(existingLineItemId ? { _id: existingLineItemId } : {}),
          product_id: item.product_id,
          variant_id: item.variant_id,
          warehouse_id: item.warehouse_id,
          product_name: item.name || item.product_name,
          sku: item.sku,
          is_production: item.is_production,
          is_taxable: lineTaxable,

          changed_unit_price: changedUnit,
          discount_on_unit_quantity: perUnitDisc,
          discount_on_total_quantity: rowDiscTotal,
          item_sub_total,


          quantity: qty,
          unit_purchase_price: asNum(item.unit_purchase_price),
          unit_retail_price: round2(unitBefore),      // original unit (pre-discount)
          // changed_unit_price: round2(changedUnit),  // after discount

          // discounts per line
          // discount_on_unit_quantity: perUnitDisc,
          // discount_on_total_quantity: rowDiscTotal,

          // totals per line
          // item_sub_total,          // post-discount, pre-tax
          total_price,             // post-discount + tax
          tax_amount,              // âœ… keep a separate field too

          images: item.images,
          customizations: {
            template_name: item.customizations?.template_name || "",
            fields: item.customizations?.fields || [],
          },
          design_id: item.design_id || "",
          design_name: item.design_name || "",
          preview_url: item.preview_url || "",
          storage_location: item.storage_location,

          // âœ… tax block uses live values
          item_tax_info,
        };
      });


      // const products = (cartItems ?? []).map((item) => {
      //   const qty = Math.max(1, asNum(item.quantity));
      //   const baseUnit = asNum(item.unit_retail_price); // unit BEFORE discount
      //   const key = keyForRow({ productId: String(item.product_id), variantId: String(item.variant_id) });
      //   const rowDiscTotal = round2(asNum(perRowAlloc[key] || 0)); // line-level discount allocation

      //   // line math (post-discount)
      //   const lineBase = baseUnit * qty;                    // pre-discount
      //   const lineAfter = Math.max(0, lineBase - rowDiscTotal); // post-discount, pre-tax
      //   const changedUnit = round2(lineAfter / qty);        // effective each after discount
      //   const perUnitDisc = round2(rowDiscTotal / qty);

      //   // âœ… Build tax info **based on changed unit**
      //   const taxInfo = buildItemTaxInfoAtCreate(
      //     {
      //       productId: String(item.product_id),
      //       variantId: String(item.variant_id),
      //       sku: String(item.sku || ""),
      //       unitPrice: changedUnit,   // use changed (effective) unit
      //       quantity: qty,
      //     },
      //     productTaxMap,
      //     shippingAddrForTax
      //   );

      //   const tax_amount = asNum((taxInfo as any)?.total_product_tax_amount || 0);

      //   // final line totals
      //   const item_sub_total = round2(changedUnit * qty);   // post-discount, pre-tax
      //   const total_price = round2(item_sub_total + tax_amount); // post-discount + tax

      //   return {
      //     product_id: item.product_id,
      //     variant_id: item.variant_id,
      //     warehouse_id: item.warehouse_id,
      //     product_name: item.name || item.product_name,
      //     sku: item.sku,
      //     is_production: item.is_production,

      //     quantity: qty,
      //     unit_purchase_price: asNum(item.unit_purchase_price),
      //     unit_retail_price: round2(baseUnit),     // original unit (pre-discount)
      //     changed_unit_price: round2(changedUnit), // âœ… added

      //     // âœ… discounts per line
      //     discount_on_unit_quantity: perUnitDisc,
      //     discount_on_total_quantity: rowDiscTotal,

      //     // âœ… totals per line
      //     item_sub_total, // pre-tax, post-discount
      //     total_price,    // tax-inclusive line total

      //     images: item.images,
      //     customizations: {
      //       template_name: item.customizations?.template_name || "",
      //       fields: item.customizations?.fields || [],
      //     },
      //     storage_location: item.storage_location,

      //     // âœ… tax block (already computed)
      //     item_tax_info: taxInfo,
      //   };
      // });

      // shipping/special from child payloads (prefer refs â€” state may lag after resolve)
      const { shippingRows, feeRows: specialRows } = getLiveCheckoutChargeRows();

      const shippingAmount = shippingRows.reduce((s: number, r: any) => s + asNum(r.shipping_amount), 0);
      const shippingTax = shippingRows.reduce((s: number, r: any) => s + asNum(r.shipping_tax_amount), 0);
      const specialAmount = specialRows.reduce((s: number, f: any) => s + asNum(f.specialfee_amount), 0);
      const specialTax = specialRows.reduce((s: number, f: any) => s + asNum(f.specialfee_tax_amount), 0);

      // ===============================================
      // âœ… PAYLOAD: Use UI values directly
      // ===============================================

      // Product totals from UI calculations
      const itemsSubtotal = round2((products || []).reduce((s: number, it: any) => s + asNum(it.item_sub_total), 0));
      const productTaxTotal = round2((products || []).reduce((s: number, it: any) => s + asNum(it?.item_tax_info?.total_product_tax_amount), 0));

      // Discount from resolve (preserve exact case)
      const discount_code = String(liveDiscountDisplay.code || couponInput).trim();
      const discount_amount = round2(Number(liveDiscountDisplay.amount || 0));
      const promo_code = discount_code;

      // Totals (what's shown in UI is what we send)
      const taxTotal = round2(uiTotalTax);
      const overallPreTax = round2(itemsSubtotal + shippingAmount + specialAmount);
      const grandTotal = round2(uiGrandTotal);

      // percentages (optional)
      const productTaxPct = itemsSubtotal > 0 ? (productTaxTotal / itemsSubtotal) * 100 : 0;
      const shippingTaxPct = shippingAmount > 0 ? (shippingTax / shippingAmount) * 100 : 0;
      const overallPct = overallPreTax > 0 ? (taxTotal / overallPreTax) * 100 : 0;

      const mapUiShipToApi = (r: any) => ({
        shipping_id: String(r.shipping__id ?? r.shipping_id ?? ""),
        shipping_name: String(r.shipping_name ?? ""),
        shipping_amount: asNum(r.shipping_amount),
        shipping_tax_percentage: asNum(r.shipping_tax_percentage),
        shipping_tax_amount: asNum(r.shipping_tax_amount),
        shipping_taxable: resolveShippingTaxableForPayload(r as Record<string, unknown>, shippingCosts),
        shipping_delivery_date: String(r.shipping_delivarydate ?? r.shipping_delivery_date ?? ""),
        shipping_delivery_time: String(r.shipping_delivery_time ?? ""),
      });

      // â€”â€”â€” office details (addresses) + foundation party fields â€”â€”â€”
      let officeInfo: any = null;

      if (selectedOfficeAgent) {
        let office = offices?.find(o => o._id === selectedOffice?.value);

        if (!office && selectedOffice?.value) {
          try {
            office = await fetchOfficeById(token, selectedOffice.value);
          } catch (err) {
            safeConsole.error("Failed to fetch office details:", err);
          }
        }

        const officeAddressToUse =
          finalBillingAddress?.address_details ||
          finalBillingAddress ||
          office?.officeAddress?.[0];

        officeInfo = office
          ? {
              _id: office._id || selectedOffice?.value,
              officeName: office.officeName || selectedOffice?.label,
              officeType: office.officeType || office.officeName || selectedOffice?.label,
              officeAddress: officeAddressToUse,
              status: true,
            }
          : null;
      } else if (selectedOffice) {
        let selectedOfficeData = offices?.find(o => o._id === selectedOffice.value);

        if (!selectedOfficeData && selectedOffice.value) {
          try {
            selectedOfficeData = await fetchOfficeById(token, selectedOffice.value);
          } catch (err) {
            safeConsole.error("Failed to fetch office details:", err);
          }
        }

        const officeAddressToUse =
          finalBillingAddress?.address_details ||
          finalBillingAddress ||
          selectedOfficeData?.officeAddress?.[0];

        officeInfo = {
          _id: selectedOffice.value,
          officeName: selectedOfficeData?.officeName || selectedOffice.label,
          officeType:
            selectedOfficeData?.officeType ||
            selectedOfficeData?.officeName ||
            selectedOffice.label,
          officeAddress: officeAddressToUse,
          status: true,
        };
      }

      const buyerContext = pickRealtorCheckoutResolveBuyer({
        values: formik.values,
        selectedOfficeAgent,
        selectedAgent,
        selectedOffice,
        selectedTeamMemberOption: selectedTeamMember
          ? {
              value: selectedTeamMember.membershipId || selectedTeamMember.value,
              membershipId: selectedTeamMember.membershipId || selectedTeamMember.value,
              globalUserId: selectedTeamMember.globalUserId,
              organizationId: selectedOffice?.value || selectedTeamMember.organizationId,
            }
          : null,
        offices,
        selectedBillingAddress: finalBillingAddress,
        selectedShippingAddress: finalShippingAddress,
        selectedOfficeBillingAddress,
        selectedOfficeShippingAddress,
        userId: userId || "",
        userName: userName || "",
        isTeammember,
        parentBrokerageId,
        brokerageOrganizationId: organizationId,
        orgname: orgname ?? undefined,
        userInfo,
      });

      const sellerIds = resolveCheckoutSellerFoundationIds({
        cartSellerOrgId,
        cartSellerMembershipId,
        cartSellerGlobalUserId,
        routeSupplierOrgId: sellerOrganizationId || supplierId,
        order: (isEdit ? editOrder : order) as {
          seller_org_id?: string;
          seller_membership_id?: string;
          seller_global_user_id?: string;
        } | null,
      });
      const sellerOrgId =
        sellerIds.organization_id ||
        sellerOrganizationId ||
        cartSellerOrgId ||
        supplierId ||
        "";

      const partyFields = buildCheckoutOrderPartyFields({
        buyerContext,
        sellerOrgId,
        sellerMembershipId: sellerIds.membership_id,
        sellerGlobalUserId: sellerIds.global_user_id,
        cartBuyerOrgId,
        cartBuyerMembershipId,
        cartBuyerGlobalUserId: cartGlobalUserId,
      });

      const inventorySource = buyerContext.inventorySource as "agent" | "office";
      const agent_name = buyerContext.agentName;
      officeInfo = officeInfo ?? buyerContext.office;

      // â€”â€”â€” payload â€”â€”â€”
      payload = {
        ...partyFields,
        buyer_type: formik.values.userType,
        signage_id: sellerOrgId,
        signage_company_name: userName || "",

        office: officeInfo,
        brokerage_firm_id:
          partyFields.brokerage_firm_org_id ??
          ((isTeammember && organizationId ? organizationId : userId) || ""),
        brokerage_firm_name:
          isTeammember && organizationId ? orgname : userInfo?.firmDetails?.companyName,
        brokerage_firm_admin:
          isTeammember && parentBrokerageName
            ? parentBrokerageName
            : (userInfo?.firstName + " " + userInfo?.lastName) || "",
        team_member_id:
          partyFields.team_member_membership_id ??
          partyFields.team_member_id ??
          formik.values.teamMemberId,
        team_member_name: formik.values.teamMemberName,
        customer_org_id: partyFields.customer_org_id || partyFields.buyer_org_id || null,
        agent_id: partyFields.agent_membership_id ?? partyFields.agent_id,
        agent_name,

        customer_first_name: formik.values.userFirstName,
        customer_last_name: formik.values.userLastName,
        customer_email: formik.values.userEmail,
        customer_phone: formik.values.userPhone,

        cart_id: cartId ?? null,
        shipping_address: finalShippingAddress,
        billing_address: finalBillingAddress,
        both_address_same: !!formik.values.isSameAsBilling,

        items: products,
        order_type: "sales",
        po_number: String(formik.values.po_number || "").trim(),
        inventory_source: inventorySource,

        discount_amount: Number(discount_amount.toFixed(2)),
        discount_code,
        promo_code,

        subtotal_amount: Number(itemsSubtotal.toFixed(2)), // post-discount, pre-tax
        shipping_amount: Number(shippingAmount.toFixed(2)),
        tax_amount: Number(taxTotal.toFixed(2)),
        total_amount: Number(grandTotal.toFixed(2)),

        order_charges: [
          {
            shipping_option: shippingRows.map(mapUiShipToApi),
            specialfee: specialRows.map((f: any) => ({
              specialfee_id: String(f.specialfee_id ?? ""),
              specialfee_name: String(f.specialfee_name ?? ""),
              specialfee_amount: asNum(f.specialfee_amount),
              specialfee_tax_percentage: asNum(f.specialfee_tax_percentage),
              specialfee_tax_amount: asNum(f.specialfee_tax_amount),
              specialfee_taxable: resolveSpecialFeeTaxableForPayload(
                f as Record<string, unknown>,
                specialFeeCatalogRef.current
              ),
              required: resolveSpecialFeeRequiredForPayload(
                f as Record<string, unknown>,
                specialFeeCatalogRef.current
              ),
            })),
          },
        ],

        order_tax_summary: {
          tax_inclusive: false,
          over_all_tax_amount: Number(taxTotal.toFixed(2)),
          over_all_tax_percentage: Number(overallPct.toFixed(4)),
          over_all_taxable_base: Number(overallPreTax.toFixed(2)),
          tax_status: editOrder?.order_tax_summary?.tax_status || "draft",
        },

        shipping_method: {
          carrier_name: "",
          service_name: "",
          tracking_number: "",
          cost: 0,
        },

        payment_method: selectedPaymentMethod || "",
        payment_status: "Pending",
        payment_info: [],
        tax_inclusive: false,
        notes: (formik.values.notes || "").trim(),
        status_history: [],
      };

      if (lastResolvedSalesRef.current) {
        payload = overlaySalesSubmitFromResolved(payload, lastResolvedSalesRef.current);
      }

     safeConsole.log(payload,"payload12345")
      const orderUrl = isEdit
        ? `${SALES_ORDER_API_BASE}${salesOrderUrls.orderUpdate(resumeOrderId)}`
        : `${SALES_ORDER_API_BASE}/orders`;
      const method = isEdit ? "PATCH" : "POST";

      let result: any;
      try {
        result = isEdit
          ? await updateOrder(token, resumeOrderId, payload)
          : await createOrder(token, payload);
      } catch (apiErr) {
        const errorMsg =
          apiErr instanceof Error ? apiErr.message : "Failed to save order";
        await handleApiError(new Error(errorMsg), {
          module: "sales_order",
          action: isEdit ? "realtor_order_checkout_update_order" : "realtor_order_checkout_create_order",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: orderUrl,
          method: method,
          requestBody: payload,
          additionalInfo: { orderId: resumeOrderId || "new" }
        }, token);
        throw new Error(errorMsg);
      }

      if (result?.error) {
        const errorMsg = result?.error || result?.message || "Failed to save order";
        await handleApiError(new Error(errorMsg), {
          module: "sales_order",
          action: isEdit ? "realtor_order_checkout_update_order" : "realtor_order_checkout_create_order",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: orderUrl,
          method: method,
          requestBody: payload,
          additionalInfo: { orderId: resumeOrderId || "new" }
        }, token);
        throw new Error(errorMsg);
      }

      const newId = isEdit
        ? resumeOrderId
        : getOrderIdFromResponse(result);
      if (!newId) {
        await handleApiError(new Error("Order saved but no id returned"), {
          module: "sales_order",
          action: isEdit ? "realtor_order_checkout_update_order" : "realtor_order_checkout_create_order",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: orderUrl,
          method: method,
          requestBody: payload,
          additionalInfo: { orderId: resumeOrderId || "new" }
        }, token);
        throw new Error("Order saved but no id returned.");
      }

      if (!isEdit) {
        const pendingFiles = pendingInstructionFilesRef.current.map((x) => x.file);
        if (pendingFiles.length > 0) {
          try {
            await uploadOrderDocumentsToOrder(newId, pendingFiles);
            pendingInstructionFilesRef.current = [];
          } catch (uploadErr) {
            showError(
              uploadErr instanceof Error
                ? `Order created, but attachments failed to upload: ${uploadErr.message}`
                : "Order created, but attachments failed to upload."
            );
          }
        }
      }

      // Send "Sales order" email notifications (centralized engine)
      try {
        const supplierId =
          (result as any)?.signage_id ||
          localStorage.getItem("supplierId") ||
          localStorage.getItem("signageId") ||
          "";
        // if (newId && supplierId && token) {
        //   await sendSalesOrderTriggerEmail({
        //     triggerName: "Brokerage Sales order",
        //     orderId: String(newId),
        //     signageId: String(supplierId),
        //     token: token || "",
        //     fallbackUserName: userName || "",
        //     fallbackCompanyName: parentBrokerageName || "",
        //   });
        // }

        const orderNumber = String((result as any)?.order_number || newId);
        const signageCompanyName = parentBrokerageName || "Signage Company";
        const placedByName = userName || "User";
        const endUserGlobalId = String(
          selectedOfficeAgent?.globalUserId ||
            selectedAgent?.globalUserId ||
            selectedTeamMember?.globalUserId ||
            ""
        ).trim();

        // âœ… Trigger: Sales Order Created - Send notifications
        if (!isEdit) {
          try {
            const { createNotification } = await import("../../services/notificationApi");
            const { getSalesOrderCreatedContent } = await import("../../services/notificationContent");
            
            // Determine who entered the order
            const enteredBy = placedByName;

            // Send notification to logged-in creator (foundation global_user_id)
            if (globalUserId && globalUserId !== endUserGlobalId && token) {
              try {
                const recipientType = resolveBrokerageCheckoutRecipientType(licenseType);
                const userContent = getSalesOrderCreatedContent({
                  orderId: orderNumber,
                  signageCompanyName,
                  recipientType,
                });
                await createNotification(globalUserId, userContent, token, recipientType);
              } catch (err) {
                safeConsole.warn("Failed to send notification to creator:", err);
              }
            }

            // Send notification to single end user (foundation global_user_id) only
            if (endUserGlobalId && token) {
              try {
                const recipientType = resolveBrokerageCheckoutBuyerRecipientType(
                  formik.values.userType
                );
                const endUserContent = getSalesOrderCreatedContent({
                  orderId: orderNumber,
                  signageCompanyName,
                  recipientType,
                });
                await createNotification(endUserGlobalId, endUserContent, token, recipientType);
              } catch (err) {
                safeConsole.warn("Failed to send notification to end user:", err);
              }
            }

            // Send notification to Signage (supplier) - internal notification
            if (supplierId && token) {
              try {
                const signageContent = getSalesOrderCreatedContent({
                  orderId: orderNumber,
                  enteredBy,
                  recipientType: "signage",
                });
                await createNotification(supplierId, signageContent, token, "signage");
              } catch (err) {
                safeConsole.warn("Failed to send notification to signage:", err);
              }
            }
          } catch (notifError) {
            safeConsole.warn("Failed to send sales order created notification:", notifError);
          }
        }
      } catch (emailError) {
        safeConsole.warn("Failed to send sales order emails:", emailError);
      }

      if (isEdit) {
        setEditOrder(result);
        showSuccess("Order updated successfully!");
        // safeNavigate(`${baseRoute}/orderDetails/${resumeOrderId}`);
      } else {
        setOrder(result);
        const orderNumber = getOrderNumberFromResponse(result);
        try {
          await linkCartToDraftOrder({
            cartId,
            orderId: newId,
            orderNumber,
            token,
          });
          setLinkedOrderIdFromCart(newId);
          setLinkedOrderNumberFromCart(orderNumber);
        } catch (linkErr) {
          safeConsole.warn("Failed to link cart to draft order:", linkErr);
        }
        showSuccess("Draft order created successfully!");
        // safeNavigate(`${baseRoute}/orderDetails/${newId}`);
      }

      return { id: newId, data: result };
    } catch (err: any) {
      safeConsole.error(err);
      showError(err?.message || "Something went wrong");
      // Send error email notification with payload if available
      // Note: payload might not be available if error occurs after order creation
      try {
        await handleApiError(err, {
          module: "sales_order",
          action: isEdit ? "realtor_order_checkout_update_order_post_save" : "realtor_order_checkout_create_order_post_save",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          requestBody: payload,
          url: isEdit
            ? `${SALES_ORDER_API_BASE}${salesOrderUrls.orderUpdate(resumeOrderId)}`
            : `${SALES_ORDER_API_BASE}/orders`,
          method: isEdit ? "PATCH" : "POST",
          additionalInfo: { orderId: resumeOrderId || "new", errorPhase: "post_save" }
        }, token);
      } catch (errorHandlerErr) {
        safeConsole.warn("Failed to log error:", errorHandlerErr);
      }
      // â— Re-throw so the caller can handle it and TS knows we never return undefined
      throw err instanceof Error ? err : new Error(String(err));
    } finally {
      setLoading(false);
    }
  }

  const fetchAddresses = async (opts?: {
    officeId?: string | null;
    teamMemberId?: string | null;
    selectedOfficeAgent?: Selected | null;
    selectedAgent?: Selected | null;
    selectedTeamMember?: any | null;
  }) => {
    const officeId = String(
      opts?.officeId ?? selectedOffice?.value ?? ""
    ).trim();
    const teamMemberId = String(
      opts?.teamMemberId ??
        opts?.selectedTeamMember?.value ??
        selectedTeamMember?.value ??
        ""
    ).trim();
    const officeAgent =
      opts?.selectedOfficeAgent !== undefined
        ? opts.selectedOfficeAgent
        : selectedOfficeAgent;
    const agent =
      opts?.selectedAgent !== undefined ? opts.selectedAgent : selectedAgent;
    const teamMember =
      opts?.selectedTeamMember !== undefined
        ? opts.selectedTeamMember
        : selectedTeamMember;
    const agentSelected = !!(officeAgent || agent);
    const isUnsetId = (id: string) =>
      !id || id === "000000000000000000000000";

    // Office (+ optional team member, no agent) â†’ office addresses. Agent â†’ membership.
    const useOfficeAddresses =
      !!officeId &&
      !isUnsetId(officeId) &&
      !agentSelected;

    let errorUrl = "";
    let errorMeta: Record<string, unknown> = {};

    try {
      let data: unknown;

      if (useOfficeAddresses) {
        data = await fetchOfficeAddresses(token, officeId);
        errorUrl = `${SALES_ORDER_API_BASE}${salesOrderUrls.officeAddresses(officeId)}`;
        errorMeta = {
          officeId,
          teamMemberId,
          buyerUserType: formik.values.userType,
        };
      } else {
        const routeId = resolveMembershipAddressesRouteId({
          selectedOfficeAgent: officeAgent,
          selectedAgent: agent,
          selectedTeamMember: teamMember,
          sessionMembershipId: membershipId,
        });
        if (!routeId) return;
        data = await fetchUserAddresses(token, routeId);
        errorUrl = `${SALES_ORDER_API_BASE}${salesOrderUrls.membershipProfileAddresses(routeId)}`;
        errorMeta = {
          buyerUserType: formik.values.userType,
          membershipId: routeId,
        };
      }

      setAddressObjects(unwrapAddressListResponse(data));
    } catch (err) {
      await handleApiError(err, {
        module: "sales_order",
        action: "realtor_order_checkout_fetch_addresses",
        userId: membershipId || undefined,
        url: errorUrl || `${SALES_ORDER_API_BASE}${salesOrderUrls.membershipProfileAddresses("")}`,
        method: "GET",
        additionalInfo: errorMeta,
      }, token);
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const buyerType = String(formik.values.userType || "").trim().toLowerCase();
    const hasOffice = !!String(selectedOffice?.value || "").trim();
    const hasTeamMember = !!String(selectedTeamMember?.value || "").trim();

    // Office + TM end user (no agent) â†’ office addresses once
    if (hasOffice && hasTeamMember && !selectedOfficeAgent && !selectedAgent) {
      void fetchAddresses();
      return;
    }

    if (selectedOfficeAgent) {
      void fetchAddresses();
    } else if (buyerType === "agent" && selectedAgent) {
      void fetchAddresses();
    } else if (hasTeamMember && !hasOffice) {
      // Team member without office â†’ membership addresses
      void fetchAddresses();
    }
  }, [
    formik.values.userType,
    selectedOffice?.value,
    selectedOfficeAgent,
    selectedAgent,
    selectedTeamMember?.value,
    token,
  ]);


  useEffect(() => {
    if (formik.values.userType === "office" && selectedOffice?.firstName) {
      formik.setFieldValue("userFirstName", selectedOffice.firstName);
    } else if (
      formik.values.userType !== "office" &&
      selectedAgent?.firstName
    ) {
      formik.setFieldValue("userFirstName", selectedAgent.firstName);
    }

    if (formik.values.userType === "office" && selectedOffice?.firstName) {
      formik.setFieldValue("userLastName", " ");
    } else if (formik.values.userType !== "office" && selectedAgent?.lastName) {
      formik.setFieldValue("userLastName", selectedAgent.lastName);
    }

    if (formik.values.userType === "office" && selectedOffice?.firstName) {
      formik.setFieldValue("userEmail", " ");
    } else if (formik.values.userType !== "office" && selectedAgent?.email) {
      formik.setFieldValue("userEmail", selectedAgent.email);
    }

    if (formik.values.userType === "office" && selectedOffice?.phone) {
      formik.setFieldValue("userPhone", selectedOffice.phone);
    } else if (formik.values.userType !== "office" && selectedAgent?.phone) {
      formik.setFieldValue("userPhone", selectedAgent.phone);
    }
  }, [formik.values.userType, selectedOffice, selectedAgent]);


  const completeOrder = async (getmethod: string) => {
    try {
      const paymentBody = {
        payment_id: "",
        total_amount: totalAmount,
        amount_paid: 0,
        amount_due: totalAmount,
        payment_date: new Date().toISOString(),
        method: getmethod,
      };

      try {
        await postOrderPayment(token, orderId, paymentBody);
      } catch {
        const errorMsg = "Failed to complete the order";
        await handleApiError(new Error(errorMsg), {
          module: "sales_order",
          action: "realtor_order_checkout_complete_order_payment",
          userId: userId || undefined,
          url: `${SALES_ORDER_API_BASE}${salesOrderUrls.orderPayment(orderId)}`,
          method: "PUT",
          additionalInfo: { orderId, method: getmethod }
        }, token);
        showError(errorMsg);
        throw new Error(errorMsg);
      }

      showSuccess("Order placed successfully!");

      // âœ… Trigger 1: Brokerage Sales Order Placed - Send notifications
      try {
        const { createNotification } = await import("../../services/notificationApi");
        const { getBrokerageSalesOrderPlacedContent } = await import("../../services/notificationContent");
        
        try {
          const orderData = await fetchOrderById(token, orderId);
          const orderNumber = orderData?.order_number || orderId;
          const orderDate = orderData?.created_at 
            ? new Date(orderData.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
            : new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

          const orderSupplierId = orderData?.signage_id || supplierId;
          const signageCompanyName =
            String(orderData?.signage_company_name || parentBrokerageName || "").trim() ||
            "Signage Company";
          const brokerageName =
            String(orgname || userInfo?.firmDetails?.companyName || userName || "").trim() ||
            "Brokerage";

          // Prefer foundation global_user_id for end-user notification target
          const endUserGlobalId = String(
            selectedOfficeAgent?.globalUserId ||
              selectedAgent?.globalUserId ||
              selectedTeamMember?.globalUserId ||
              (orderData as any)?.agent_global_user_id ||
              (orderData as any)?.buyer_global_user_id ||
              (orderData as any)?.team_member_global_user_id ||
              ""
          ).trim();

          // Send notification to logged-in creator (foundation global_user_id)
          if (globalUserId && globalUserId !== endUserGlobalId && token) {
            try {
              const recipientType = resolveBrokerageCreatorPlacedRecipientType(licenseType);
              const userContent = getBrokerageSalesOrderPlacedContent({
                orderId: orderNumber,
                signageCompanyName,
                orderDate,
                brokerageName,
                recipientType,
              });
              await createNotification(globalUserId, userContent, token, recipientType);
            } catch (err) {
              safeConsole.warn("Failed to send notification to creator:", err);
            }
          }

          // Send notification to single end user only
          if (endUserGlobalId && token) {
            try {
              const recipientType = resolveBrokerageEndUserPlacedRecipientType({
                selectedOfficeAgent,
                selectedAgent,
                selectedTeamMember,
                orderData,
              });
              const endUserContent = getBrokerageSalesOrderPlacedContent({
                orderId: orderNumber,
                signageCompanyName,
                orderDate,
                brokerageName,
                recipientType,
              });
              await createNotification(endUserGlobalId, endUserContent, token, recipientType);
            } catch (err) {
              safeConsole.warn("Failed to send notification to end user:", err);
            }
          }

          // Notify Signage Admin/Team Members
          if (orderSupplierId) {
            try {
              const signageTeamData = await searchUsersDatatable(token, {
                page: 1,
                limit: 100,
                filters: {
                  signage_company_id: orderSupplierId,
                  licenseType: ["SIGNAGE_FIRM", "SIGNAGE_TEAM_MEMBER"],
                  status: "Active",
                },
                fields: ["id", "email", "licenseType"],
              });
              const signageTeamMembers = Array.isArray(signageTeamData?.data) ? signageTeamData.data : [];

              for (const member of signageTeamMembers) {
                  const memberId = member.id || member._id;
                  const memberLicenseType = member.licenseType || "SIGNAGE_TEAM_MEMBER";
                  const memberRecipientType = memberLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";

                  if (memberId && token) {
                    try {
                      const signageContent = getBrokerageSalesOrderPlacedContent({
                        orderId: orderNumber,
                        signageCompanyName,
                        orderDate,
                        brokerageName,
                        recipientType: memberRecipientType,
                      });
                      await createNotification(memberId, signageContent, token, memberRecipientType);
                    } catch (err) {
                      safeConsole.warn(`Failed to send notification to signage team member ${memberId}:`, err);
                    }
                  }
                }
            } catch (signageErr) {
              safeConsole.warn("Failed to fetch signage team members for notification:", signageErr);
            }
          }
        } catch (orderErr) {
          safeConsole.warn("Failed to fetch order for notification:", orderErr);
        }
      } catch (notifError) {
        safeConsole.warn("Failed to send order placed notification:", notifError);
        // Don't block the order completion if notification fails
      }

      // safeNavigate(`${baseRoute}/orders`);
    } catch (error) {
      const errMessage =
        (error as Error).message || "An unknown error occurred";
      safeConsole.error("Order Failed:", errMessage);
      showError(`Error: ${errMessage}`);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (offices && offices.length > 0) {
      const office = offices.find((office) => office?._id === editOrder?.office?._id);
      // safeConsole.log("Selected Office in useEffect :", office);
      office &&
        setSelectedOffice({
          value: office?._id,
          label: office?.officeName,
          firstName: office?.officeName,
          lastName: office?.officeName,
          email: "",
          phone: office?.officeAddress?.[0]?.phone,
        });
    }

  }, [offices])

  const fetchPaymentMethods = async (id: string) => {
    // const id = order?.inventory_source === "office" ? order?.team_member_id : order?.agent_id
    try {
      const data = await fetchPaymentMethodsBySupplierAndUser(token, supplierId, id);
      setPaymentMethod(data);
    } catch (err) {
      // await handleApiError(err, {
      //   module: "sales_order",
      //   action: "realtor_order_checkout_fetch_payment_methods",
      //   userId: userId || undefined,
      //   url: `${process.env.REACT_APP_API_URL}/payment-methods/by-supplier/${supplierId}/by-user/${id}`,
      //   method: "GET",
      //   additionalInfo: { supplierId, userId: id }
      // }, token);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };
  // useEffect(() => {
  //   fetchPaymentMethods();
  //   safeConsole.log("Order in useEffect", order);
  // }, [order]);

  useEffect(() => {
    const loadShippingCosts = async () => {
      try {
        const data = await fetchShippingCosts(token, sellerOrganizationId || supplierId);
        setShippingCosts(data);
      } catch (err) {
        await handleApiError(err, {
          module: "sales_order",
          action: "realtor_order_checkout_fetch_shipping_costs",
          userId: userId || undefined,
          url: `${SALES_ORDER_API_BASE}${salesOrderUrls.shippingCosts(sellerOrganizationId || supplierId || "")}`,
          method: "GET",
          additionalInfo: { supplierId }
        }, token);
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    loadShippingCosts();
  }, [sellerOrganizationId, supplierId, token]);

  const removeProductRow = (index: number) => {
    setCartItems((prev) => prev.filter((_, i) => i !== index));
  };

  // normalize (string | {url}) â†’ id string
  const idOf = (x?: any): string => {
    if (!x) return "";
    if (typeof x === "string") return x;
    if (typeof x?.url === "string") return x.url;
    return "";
  };

  // gather ALL ids we might need to display (product row image + custom file fields)
  const allImageIds = React.useMemo(() => {
    const ids = new Set<string>();
    (cartItems || []).forEach((row) => {
      const firstId = idOf(row?.images?.[0]);
      if (firstId) ids.add(firstId);
      // If this item has a customized design, collect the preview_url ID for resolution
      if (row?.design_id && typeof row?.preview_url === "string" && row.preview_url.trim()) {
        ids.add(row.preview_url.trim());
      }
      const fields = row?.customizations?.fields || [];
      fields.forEach((f: any) => {
        if (String(f?.field_type || "").toLowerCase() !== "file") return;
        const raw = f?.value;
        const fid =
          typeof raw === "string"
            ? raw.trim()
            : raw && typeof raw === "object" && raw.url
              ? String(raw.url).trim()
              : "";
        if (fid) ids.add(fid);
      });
    });

    return Array.from(ids);
  }, [cartItems]);

  const pickerStudioBatchVariantPreviews = useMemo(
    () => studioBatchVariantPreviewsFromCartLines(cartItems || []),
    [cartItems]
  );

  const dataForMap = React.useMemo(() => {
    if (allImageIds.length === 0) return [];
    return [
      {
        product: {
          variants: [
            { images: allImageIds.map((url) => ({ url })) },
          ],
        },
      },
    ];
  }, [allImageIds]);
  // presigned urls map
  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);
  const buildImageUrl = (raw?: string) => {
    if (!raw) return "";
    const cleaned = raw.replace(/\\/g, "/");
    if (/^https?:\/\//i.test(cleaned)) return cleaned;
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    const path = cleaned.replace(/^\/+/, "");
    return `${base}/${encodeURI(path)}`;
  };
  const mobileColumns: ColumnDef<CartItemRow, any>[] = [
    // Product card area
    ch.display({
      id: "product",
      header: () => "Product",
      cell: ({ row }) => {
        const item = row.original;
        const fields = item?.customizations?.fields || [];
        const isFile = (f: any) => String(f?.field_type || "").toLowerCase() === "file";
        const firstFileVal = (raw: any): string => {
          if (!raw) return "";
          if (typeof raw === "string") return raw.trim();
          if (raw && typeof raw === "object" && raw.url) return String(raw.url).trim();
          return "";
        };
        const hasValue = (f: any): boolean => {
          if (isFile(f)) return !!firstFileVal(f?.value);
          return String(f?.value ?? "").trim().length > 0;
        };
        const textFields = fields.filter((f: any) => !isFile(f) && hasValue(f));
        const fileFields = fields.filter((f: any) => isFile(f) && hasValue(f));
        const hasAnyCustom = textFields.length > 0 || fileFields.length > 0;
        const title = item.name || item.product_name;
        const firstId = idOf(item?.images?.[0]);
        const imgSrc =
          (item?.design_id && item?.preview_url
            ? getImageSrc(item.preview_url, imageUrlMap) || buildImageUrl(item.preview_url)
            : "") ||
          getImageSrc(firstId, imageUrlMap) ||
          getImageSrc(item?.image_url, imageUrlMap) ||
          buildImageUrl(firstId || item?.image_url) ||
          "";
        const imgOk =
          !!imgSrc &&
          imgSrc.trim() !== "" &&
          !imgSrc.endsWith("/undefined") &&
          !imgSrc.includes("/null") &&
          !imgSrc.endsWith("/");
        const showSrc = imgOk ? imgSrc : "";

        return (
          <div className="d-flex gap-3">
            <div style={{ width: 72, height: 72, flexShrink: 0 }}>
              <img
                src={showSrc || imagecustomRounded}
                alt=""
                className="img-fluid rounded "
                style={{ objectFit: "contain", width: "100%", height: "100%" }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = imagecustomRounded;
                }}
              />
            </div>
            <div>
              <div className="fw-semibold text-uppercase mb-1 wrap-anywhere">{title}</div>
              <div className="text-muted small">{item.sku}</div>

              {hasAnyCustom ? (
                <div className="mt-2 custom-fields-container">
                  {textFields.map((field: any, idx: number) => (
                    <div key={`cf-t-${field?._id || field?.field_name || idx}`} className="mb-1" style={{ wordBreak: "break-word", maxWidth: 380 }}>
                      <strong className="custom-cells wrap-anywhere">{field.field_name}:</strong>{" "}
                      <span className="wrap-anywhere">{String(field?.value ?? "").trim()}</span>
                    </div>
                  ))}
                  {fileFields.map((field: any, idx: number) => {
                    const fid = firstFileVal(field?.value);
                    const href = getImageSrc(fid, imageUrlMap) || buildImageUrl(fid);
                    return (
                      <div key={`cf-f-${field?._id || field?.field_name || idx}`} className="mb-2" style={{ wordBreak: "break-word", maxWidth: 380 }}>
                        <div className="text-muted small wrap-anywhere">{field.field_name}</div>
                        {href ? (
                          <a href={href} download target="_blank" rel="noopener noreferrer">
                            <img
                              src={href}
                              alt={field.field_name}
                              className="rounded"
                              style={{ maxWidth: 88, height: "auto", cursor: "pointer" }}
                            />
                          </a>
                        ) : /^[0-9a-f]{24}$/i.test(fid) ? (
                          <span className="text-muted small">Loadingâ€¦</span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        );
      },
    }),

    // Quantity (editable)
    ch.display({
      id: "qty",
      header: () => "Quantity",
      cell: ({ row }) => {
        const i = row.index;
        return (
          <input
            type="number"
            className="form-control text-center"
            min={1}
            value={row.original.quantity || 1}
            onChange={(e) => handleQuantityChange(i, e)}
          />
        );
      },
    }),

    ch.display({
      id: "unit_price",
      header: () => "Unit Price",
      cell: ({ row }) => {
        const v = Number(row.original.unit_retail_price || row.original.unit_purchase_price || 0);
        return `$${v.toFixed(2)}`;
      },
    }),

    ch.display({
      id: "changed_price",
      header: () => "Changed Price",
      cell: ({ row }) => {
        const unitRetail = Number(row.original.unit_retail_price || row.original.unit_purchase_price || 0);
        const v = Number(row.original.changed_unit_price) || unitRetail;
        return `$${v.toFixed(2)}`;
      },
    }),

    ch.display({
      id: "discount",
      header: () => "Discount",
      cell: ({ row }) => {
        const item = row.original;
        const key = `${item.product_id}:${item.variant_id}`;
        const qty = Math.max(1, Number(item.quantity || 1));
        const unitRetail = Number(item.unit_retail_price || item.unit_purchase_price || 0);
        const changedUnit = Number(item.changed_unit_price ?? unitRetail);
        const { lineDiscount } = getAgentRealtorCheckoutLinePricing({
          item,
          qty,
          unitRetail,
          changedUnitPrice: changedUnit,
          lineKey: key,
        });
        return <DiscountAmountDisplay amount={lineDiscount} />;
      },
    }),

    ch.display({
      id: "tax",
      header: () => "Tax",
      cell: ({ row }) => {
        const key = `${row.original.product_id}:${row.original.variant_id}`;
        const skuKey = String(row.original.sku || "");
        const taxRaw = (skuKey && productTaxMap?.[skuKey] !== undefined ? productTaxMap[skuKey] : undefined) ?? productTaxMap?.[key];
        const tax = Number.isFinite(taxRaw) ? Number(taxRaw || 0) : Number(row.original?.tax_details?.tax_amount || 0);
        return `$${tax.toFixed(2)}`;
      },
    }),

    ch.display({
      id: "total",
      header: () => "Total Amount",
      cell: ({ row }) => {
        const item = row.original;
        const qty = Number(item.quantity || 0);
        const key = `${item.product_id}:${item.variant_id}`;
        const skuKey = String(item.sku || "");
        const unitRetail = Number(item.unit_retail_price || item.unit_purchase_price || 0);
        const changedUnit = Number(item.changed_unit_price ?? unitRetail);
        const taxRaw = (skuKey && productTaxMap?.[skuKey] !== undefined ? productTaxMap[skuKey] : undefined) ?? productTaxMap?.[key];
        const tax = Number.isFinite(taxRaw) ? Number(taxRaw || 0) : Number(item?.tax_details?.tax_amount || 0);
        const { lineTotal } = getAgentRealtorCheckoutLinePricing({
          item,
          qty,
          unitRetail,
          changedUnitPrice: changedUnit,
          lineKey: key,
          rowTax: tax,
        });
        return `$${lineTotal.toFixed(2)}`;
      },
    }),

    // Actions
    // ch.display({
    //   id: "actions",
    //   header: () => "Action",
    //   cell: ({ row }) => {
    //     const i = row.index;
    //     return (
    //       <div className="d-flex justify-content-end gap-2">
    //         {i === cartItems.length - 1 && (
    //           <button type="button" className="btn btn-sm btn-outline-success" onClick={() => setPickerOpen(true)}>
    //             <i className="ri-add-line" /> Add
    //           </button>
    //         )}
    //         {cartItems.length > 1 && (
    //           <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeProductRow(i)}>
    //             <i className="ri-close-line" /> Remove
    //           </button>
    //         )}
    //       </div>
    //     );
    //   },
    // }),
  ];

  const mobileData = useMemo<CartItemRow[]>(() => cartItems, [cartItems]);

  const mobileTable = useReactTable({
    data: mobileData,
    columns: mobileColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  // ============ CLEARING HELPER FUNCTIONS ============
  // Clears just the name/email/phone + addresses
  const clearPersonAndAddresses = () => {
    formik.setFieldValue("userFirstName", "");
    formik.setFieldValue("userLastName", "");
    formik.setFieldValue("userEmail", "");
    formik.setFieldValue("userPhone", "");
    formik.setFieldValue("billingAddressId", "");
    formik.setFieldValue("shippingAddressId", "");
    formik.setFieldValue("isSameAsBilling", false);

    setAddressObjects([]);
    setSelectedBillingAddress(null);
    setSelectedShippingAddress(null);
    setSelectedOfficeBillingAddress(null);
    setSelectedOfficeShippingAddress(null);
  };

  const clearTeamMember = () => {
    formik.setFieldValue("teamMemberId", "");
    formik.setFieldValue("teamMemberName", "");
    setSelectedTeamMember(null);
    setOffices([]);
  };

  const clearOffice = () => {
    formik.setFieldValue("officeId", "");
    setSelectedOffice(null);
    setOfficeAgents([]);
  };

  const clearOfficeAgent = () => {
    formik.setFieldValue("officeAgentId", "");
    setSelectedOfficeAgent(null);
  };

  const clearAgent = () => {
    formik.setFieldValue("agent_id", "");
    setSelectedAgent(null);
  };

  // âœ… minimal, TS-safe, easy to read
  const hasRequiredContactInfo =
    Boolean(String(formik.values.userEmail || "").trim()) &&
    Boolean(String(formik.values.userPhone || "").trim());
  const hasBilling = !!formik.values.billingAddressId;
  const hasShipping = !!(formik.values.isSameAsBilling || formik.values.shippingAddressId);

  // âœ… Validation: Check if shipping option is selected
  const hasShippingOption = React.useMemo(() => {
    const shippingOptions = shipPayload?.shipping_option || [];
    return shippingOptions.length > 0;
  }, [shipPayload]);

  // âœ… Validation: Check if all required special fees are selected
  const hasRequiredSpecialFees = React.useMemo(() => {
    const specialFees = feePayload?.specialfee || [];
    const selectedFeeIds = feePayload?.selected_fee_ids || [];
    const requiredFees = specialFees.filter((f: any) => f.required === true);

    if (requiredFees.length === 0) return true; // No required fees
    return requiredFees.every((rf: any) => selectedFeeIds.includes(rf.specialfee_id));
  }, [feePayload]);

  // final flag
  // const canSaveOrder = hasBilling && hasShipping && hasShippingOption && hasRequiredSpecialFees && !order;
  const canSaveOrder =
    hasRequiredContactInfo &&
    hasBilling &&
    hasShipping &&
    hasShippingOption &&
    hasRequiredSpecialFees;

  React.useEffect(() => {
    if (!editOrder?.id) return;
    const shipRowsInit = getShippingRowsFromOrder(editOrder);
    if (!shipPayload?.shipping_option?.length && shipRowsInit.length) {
      const selection = getInitialShippingSelectionFromOrder(editOrder);
      const isUps = Boolean(selection?.ups_service_code);
      const nextShip = {
        shipping_option: shipRowsInit,
        isUps,
        isManual: !isUps,
        selected_ids: selection
          ? {
              manual_shipping_id: selection.manual_shipping_id,
              ups_service_code: selection.ups_service_code,
              integration_id: selection.integration_id,
              shipper_address_id: selection.shipper_address_id,
            }
          : undefined,
      } as ShipPayload;
      shipPayloadRef.current = nextShip;
      setShipPayload(nextShip);
    }
  }, [editOrder?.id, shipPayload?.shipping_option?.length]);

  // hydrate SpecialFee from edit order once
  React.useEffect(() => {
    if (!editOrder?.id) return;
    const feeRowsInit = getSpecialFeeRowsFromOrder(editOrder);
    if (!feePayload?.specialfee?.length && feeRowsInit.length) {
      const feeIds = feeRowsInit
        .map((r: any) => String(r.specialfee_id ?? ""))
        .filter(Boolean);
      const nextFee = {
        specialfee: feeRowsInit,
        selected_fee_ids: feeIds,
        total_amount: 0,
        total_tax_amount: 0,
        total_tax_percentage: 0,
      } as FeePayload;
      feePayloadRef.current = nextFee;
      setFeePayload(nextFee);
    }
  }, [editOrder?.id, feePayload?.specialfee?.length]);

  function computeSavedTotals(o: any) {
    const ch = (o?.order_charges?.[0] ?? {}) as any;
    const ship = Array.isArray(ch?.shipping_option) ? ch.shipping_option : [];
    const fee = Array.isArray(ch?.specialfee) ? ch.specialfee : [];

    const sub = n(o?.subtotal_amount ?? o?.pricing?.sub_total ??
      (Array.isArray(o?.items) ? o.items.reduce((s: number, it: any) => s + n(it?.total_price ?? n(it?.unit_retail_price) * n(it?.quantity)), 0) : 0));

    const prodTax = Array.isArray(o?.items) ? o.items.reduce((s: number, it: any) => s + n(it?.item_tax_info?.total_product_tax_amount), 0) : 0;
    const shipTax = ship.reduce((s: any, r: any) => s + n(r?.shipping_tax_amount), 0);
    const feeTax = fee.reduce((s: any, r: any) => s + n(r?.specialfee_tax_amount), 0);

    const taxTot = n(o?.order_tax_summary?.over_all_tax_amount ?? (prodTax + shipTax + feeTax));
    const shipAmt = ship.reduce((s: any, r: any) => s + n(r?.shipping_amount), 0);
    const feeAmt = fee.reduce((s: any, r: any) => s + n(r?.specialfee_amount), 0);
    const grand = n(o?.total_amount ?? o?.pricing?.grand_total ?? (sub + shipAmt + feeAmt + taxTot));
    // safeConsole.log("computeSavedTotals:", { sub, shipAmt, feeAmt, prodTax, shipTax, feeTax, taxTot, grand, ship, fee });
    return {
      subTotal: sub, shipping: shipAmt, specialFee: feeAmt,
      taxBreakdown: { product: prodTax, shipping: shipTax, specialFee: feeTax },
      taxTotal: taxTot, grandTotal: grand,
      _savedShipRows: normShip(ship), _savedFeeRows: normFee(fee),
    };
  }

  const [savedTotals, setSavedTotals] = React.useState<any>(null);

  // snapshot once when editOrder is loaded
  React.useEffect(() => { if (editOrder && !savedTotals) setSavedTotals(computeSavedTotals(editOrder)); }, [editOrder, savedTotals]);

  React.useEffect(() => {
    if (!savedTotals) return;
    const curShip = normShip(shipPayload?.shipping_option ?? []);
    const curFee = normFee(feePayload?.specialfee ?? []);
    const changed =
      JSON.stringify(savedTotals._savedShipRows) !== JSON.stringify(curShip) ||
      JSON.stringify(savedTotals._savedFeeRows) !== JSON.stringify(curFee);
    if (changed) setUseLiveTotals(true);
  }, [savedTotals, shipPayload?.shipping_option, feePayload?.specialfee]);

  // destination used for taxes (shipping if present, else billing)
  // âœ… Priority: If selectedOffice is set, use office addresses; otherwise use agent addresses
  const taxAddressObj = React.useMemo(() => {
    const isOffice =
      !!selectedOffice &&
      !selectedOfficeAgent &&
      !selectedAgent;
    
    if (formik.values.isSameAsBilling) {
      // Same as billing: use billing address
      if (isOffice) {
        return selectedOfficeBillingAddress;
      } else {
        return selectedBillingAddress;
      }
    } else {
      // Different addresses: prefer shipping, fallback to billing
      if (isOffice) {
        return selectedOfficeShippingAddress || selectedOfficeBillingAddress;
      } else {
        return selectedShippingAddress || selectedBillingAddress;
      }
    }
  }, [
    formik.values.isSameAsBilling,
    selectedOffice,
    selectedOfficeAgent,
    selectedAgent,
    selectedBillingAddress,
    selectedShippingAddress,
    selectedOfficeBillingAddress,
    selectedOfficeShippingAddress
  ]);

  const selectedProductsWithDims = useMemo(() => {
    const items = asArray(cartItems);
    const products: ProductObj[] = asArray(productobj) as ProductObj[]; // <-- normalize
    return items.map((ci: any) => {
      const d = ci?.package_dimensions ?? ci?.package_dimensions ?? {};
      const dims = {
        length: Number(d.length ?? 0) || 0,
        width: Number(d.width ?? 0) || 0,
        height: Number(d.height ?? 0) || 0,
        weight: Number(d.weight ?? 0) || 0,
        weight_type: String(d.weight_type ?? "LBS"),
      };

      return {
        productId: ci.product_id,
        variantId: ci.variant_id,
        quantity: Number(ci.quantity ?? 0) || 0,
        package_dimensions: dims,
      };
    });
  }, [cartItems, productobj]);
  // Unique key for a cart line

  const shippingProductsKey = useMemo(() => JSON.stringify(
    selectedProductsWithDims.map((p: any) => ({
      productId: p.productId,
      variantId: p.variantId,
      quantity: Number(p.quantity || 0),
      length: Number(p.package_dimensions?.length || 0),
      width: Number(p.package_dimensions?.width || 0),
      height: Number(p.package_dimensions?.height || 0),
      weight: Number(p.package_dimensions?.weight || 0),
      weightType: String(p.package_dimensions?.weight_type || "LBS"),
    }))
  ), [selectedProductsWithDims]);
  const keyFor = (o: { product_id?: string; variant_id?: string; productId?: string; variantId?: string }) =>
    `${String(o.product_id ?? o.productId ?? "")}:${String(o.variant_id ?? o.variantId ?? "")}`;

  const hasZeroOrNegativeLine = React.useMemo(() => {
    return (cartItems || []).some((ci) => {
      const key = keyFor(ci);
      const qty = Number(ci.quantity || 0);
      if (qty <= 0) return true;
      const base = Number(ci.changed_unit_price || 0) * qty;
      const disc = Number(perItemCouponMap[key] || 0);
      const net = base - disc;
      return net <= 0;
    });
  }, [cartItems, perItemCouponMap]);




  // Friendly message (shown under table) and you can also reuse this to disable submit
  const zeroOrNegativeMsg =
    hasZeroOrNegativeLine
      ? "Subtotal cannot be zero or negative after discount. Please adjust quantity or unit price, or remove the coupon."
      : null;


  // shipping/special-fee amounts/taxes from child payloads
  const shippingAmount = React.useMemo(() => {
    const rows = Array.isArray(shipPayload?.shipping_option) ? shipPayload!.shipping_option! : [];
    return rows.reduce((s, r) => s + Number(r?.shipping_amount || 0), 0);
  }, [shipPayload?.shipping_option]);

  const shippingTaxAmount = React.useMemo(() => {
    const rows = Array.isArray(shipPayload?.shipping_option) ? shipPayload!.shipping_option! : [];
    return rows.reduce((s, r) => s + Number(r?.shipping_tax_amount || 0), 0);
  }, [shipPayload?.shipping_option]);

  const specialFeeAmount = React.useMemo(() => {
    const rows = Array.isArray(feePayload?.specialfee) ? feePayload!.specialfee! : [];
    return rows.reduce((s, r) => s + Number(r?.specialfee_amount || 0), 0);
  }, [feePayload?.specialfee]);

  const specialFeeTaxAmount = React.useMemo(() => {
    const rows = Array.isArray(feePayload?.specialfee) ? feePayload!.specialfee! : [];
    return rows.reduce((s, r) => s + Number(r?.specialfee_tax_amount || 0), 0);
  }, [feePayload?.specialfee]);


  // ---------- Order Totals VM (server defaults â†’ live when available) ----------
  const num = (v: any) => Number(v || 0);

  // Use the loaded order when editing; otherwise the newly created order
  const sourceOrder = editOrder || order;

  // SERVER totals (from GET/DB)
  const srvSubProducts = num(sourceOrder?.subtotal_amount);
  const srvTotalAmount = num(sourceOrder?.total_amount);
  const srvDiscountTotal = num(sourceOrder?.discount_amount || cartHeader?.discount_amount);
  const srvTaxOverall = num(sourceOrder?.order_tax_summary?.over_all_tax_amount);

  // SERVER charges
  const srvShipRows = Array.isArray(sourceOrder?.order_charges?.[0]?.shipping_option)
    ? sourceOrder!.order_charges![0]!.shipping_option! : [];
  const srvFeeRows = Array.isArray(sourceOrder?.order_charges?.[0]?.specialfee)
    ? sourceOrder!.order_charges![0]!.specialfee! : [];

  const srvShipAmt = srvShipRows.reduce((s: number, r: any) => s + num(r.shipping_amount), 0);
  const srvShipTax = srvShipRows.reduce((s: number, r: any) => s + num(r.shipping_tax_amount), 0);
  const srvFeeAmt = srvFeeRows.reduce((s: number, r: any) => s + num(r.specialfee_amount), 0);
  const srvFeeTax = srvFeeRows.reduce((s: number, r: any) => s + num(r.specialfee_tax_amount), 0);

  // LIVE from UI/engines/children (already managed in this file)
  const liveSubProducts = num(productTotals?.subTotal);
  const liveProdTax = num(productTotals?.productTax);
  const liveShipAmt = num(shippingAmount);
  const liveShipTax = num(shippingTaxAmount);
  const liveFeeAmt = num(specialFeeAmount);
  const liveFeeTax = num(specialFeeTaxAmount);

  // Switch to LIVE numbers once any live source is non-zero
  const usingLive =
    (liveSubProducts || liveProdTax || liveShipAmt || liveShipTax || liveFeeAmt || liveFeeTax) > 0;

  // âœ… Subtotal (products, post-discount, pre-tax)
  // Use live calculation when coupon is active
  const subTotalProducts = usingLive ? liveSubProducts : srvSubProducts;

  // Charges to show
  const shippingAmountToShow = usingLive ? liveShipAmt : srvShipAmt;
  const specialFeeAmountToShow = usingLive ? liveFeeAmt : srvFeeAmt;

  // Tax breakdown to show
  const productTaxToShow = usingLive ? liveProdTax : num(srvTaxOverall - srvShipTax - srvFeeTax);
  const shippingTaxToShow = usingLive ? liveShipTax : srvShipTax;
  const feeTaxToShow = usingLive ? liveFeeTax : srvFeeTax;

  // Total tax
  const totalTaxAll = num(productTaxToShow) + num(shippingTaxToShow) + num(feeTaxToShow);

  // ===============================================
  // âœ… SIMPLIFIED UI TOTALS - What you see is what you send
  // ===============================================

  // 1ï¸âƒ£ Subtotal (before discount) = Sum of (qty Ã— unit_retail_price)
  const uiSubtotal = React.useMemo(() => {
    return round2((cartItems || []).reduce((sum, item) => {
      return sum + (num(item.quantity) * num(item.changed_unit_price));
    }, 0));
  }, [cartItems]);

  // 2ï¸âƒ£ Total Discount from resolve
  const uiDiscount = React.useMemo(() => {
    return round2(Number(liveDiscountDisplay.amount || 0));
  }, [liveDiscountDisplay.amount, resolvedTotalsTick]);

  // 3ï¸âƒ£ Shipping & Special Fee (from child components)
  const uiShipping = num(shippingAmountToShow || 0);
  const uiShippingTax = num(shippingTaxToShow || 0);
  const uiSpecialFee = num(specialFeeAmountToShow || 0);
  const uiSpecialFeeTax = num(feeTaxToShow || 0);

  // 4ï¸âƒ£ Product Tax (from tax engine on discounted amounts)
  const uiProductTax = num(productTaxToShow || 0);

  // 5ï¸âƒ£ Total Tax
  const uiTotalTax = React.useMemo(() => {
    const fromResolved = lastResolvedSalesRef.current?.order_tax_summary?.over_all_tax_amount;
    if (fromResolved != null && Number.isFinite(Number(fromResolved))) {
      return round2(Number(fromResolved));
    }
    const fromBreakdown =
      Number(taxBreakdown.product || 0) +
      Number(taxBreakdown.shipping || 0) +
      Number(taxBreakdown.specialFee || 0);
    if (fromBreakdown > 0) return round2(fromBreakdown);
    return round2(uiProductTax + uiShippingTax + uiSpecialFeeTax);
  }, [uiProductTax, uiShippingTax, uiSpecialFeeTax, taxBreakdown, resolvedTotalsTick]);

  // 6ï¸âƒ£ Grand Total = subtotal - discount + shipping + special fee + all taxes
  const uiGrandTotal = round2(
    uiSubtotal - uiDiscount + uiShipping + uiSpecialFee + uiTotalTax
  );

  const shippingRowsForTotalsLabel = React.useMemo(() => {
    const live = shipPayload?.shipping_option;
    if (Array.isArray(live) && live.length > 0) return live;
    const ch = sourceOrder?.order_charges?.[0] as any;
    return Array.isArray(ch?.shipping_option) ? ch.shipping_option : [];
  }, [shipPayload?.shipping_option, sourceOrder?.order_charges]);

  const isUpsShippingForTotalsLabel = React.useMemo(() => {
    const live = shipPayload?.shipping_option;
    if (Array.isArray(live) && live.length > 0) return !!shipPayload?.isUps;
    const ch = sourceOrder?.order_charges?.[0] as any;
    const rows = Array.isArray(ch?.shipping_option) ? ch.shipping_option : [];
    const so = rows[0] as any;
    const rawId = String(so?.shipping_id ?? so?.shipping__id ?? "");
    if (rawId && /^[0-9A-Z]{2,3}$/.test(rawId) && rawId.length <= 3 && rawId !== "custom-manual") return true;
    return /^ups$/i.test(String(so?.shipping_type ?? "").trim());
  }, [shipPayload?.shipping_option, shipPayload?.isUps, sourceOrder?.order_charges]);

  const specialFeeRowsForTotalsLabel = React.useMemo(() => {
    const live = feePayload?.specialfee;
    if (Array.isArray(live) && live.length > 0) return live;
    const ch = sourceOrder?.order_charges?.[0] as any;
    return Array.isArray(ch?.specialfee) ? ch.specialfee : [];
  }, [feePayload?.specialfee, sourceOrder?.order_charges]);

  const shippingChargeTotalsLabel = React.useMemo(
    () => formatShippingChargeOrderTotalsLabel(shippingRowsForTotalsLabel, { isUps: isUpsShippingForTotalsLabel }),
    [shippingRowsForTotalsLabel, isUpsShippingForTotalsLabel]
  );
  const specialFeeBreakdownDisplay = React.useMemo(
    () => buildSpecialFeeCostBreakdownDisplay(specialFeeRowsForTotalsLabel),
    [specialFeeRowsForTotalsLabel]
  );

  // âœ… Compute effective createdById based on user selection priority
  // Use membership ids (never brokerage session organizationId) â€” vendor org is sellerOrganizationId.
  const effectiveCouponCreatedById = React.useMemo(() => {
    let id = "";

    // Priority: Office Agent > Team Member (for office path) > Direct Agent > logged-in membership
    if (selectedOfficeAgent?.value) {
      id = selectedOfficeAgent.value;
    } else if (selectedOffice?.value && selectedTeamMember?.value) {
      id = selectedTeamMember.value;
    } else if (selectedAgent?.value) {
      id = selectedAgent.value;
    } else {
      // Brokerage firm self-checkout: buyer membership (agent checkout uses membershipId the same way)
      id = membershipId || userId || "";
    }
    return id;
  }, [selectedOfficeAgent, selectedAgent, selectedOffice, selectedTeamMember, membershipId, userId]);

  // ---- Coupon expiration check ----
  const couponProducts = React.useMemo(
    () => (cartItems || []).map(it => ({
      product_id: String(it.product_id || ""),
      variant_id: String(it.variant_id || ""),
    })),
    [cartItems]
  );

  const { checkAndProceed: checkCouponExpiration, checking: checkingCoupon, Modal: CouponExpirationModal } = useCouponExpirationCheck({
    token: token || "",
    supplierId: supplierId || "",
    customerId: effectiveCouponCreatedById || "",
    discountCode: (liveDiscountDisplay.code || couponInput).trim(),
    products: couponProducts,
    onRemoveCoupon: () => {
      setPerItemCouponMap({});
      showSuccess("Expired coupon removed. Proceeding with original prices.");
    },
  });

  const onConfirmAndPay = async () => {
    if (isAnythingBusy) return;

    // âœ… Check coupon expiration first
    await checkCouponExpiration(async () => {
      // This callback runs only if coupon is valid OR user clicked OK on expiration modal
      await confirmAndPayProcess();
    });
  };

  const openCheckoutPaymentModal = (draft: { id: string; data: Record<string, unknown> }) => {
    const paymentOrder = {
      ...(isEdit && editOrder ? editOrder : order ? order : {}),
      ...draft.data,
    } as Record<string, unknown>;
    setCheckoutPaymentOrder(paymentOrder);
    setCheckoutPaymentOpen(true);
  };

  const confirmAndPayProcess = async () => {
    if (isAnythingBusy) return;
    setLoading(true);
    try {
      // 1) Save draft first (now guaranteed to return {id,data} or throw)
      const draft = await createDraftOrder();

      if (draft?.data) {
        openCheckoutPaymentModal({
          id: draft.id,
          data: draft.data as Record<string, unknown>,
        });
      }
    } catch (e: any) {
      showError(e?.message || "Failed to submit and calculate tax");
    } finally {
      setLoading(false);
    }
  };

  const initialFeeIds = React.useMemo<string[] | undefined>(() => {
    if (isEdit) {
      return getInitialSpecialFeeIdsFromOrder(editOrder);
    } else {
      const rows = cartHeader?.order_charges?.specialfee ?? [];
      const ids = Array.isArray(rows) ? rows.map((r: any) => String(r?.specialfee_id)).filter(Boolean) : [];
      // also consider backend's selected_fee_ids, if present
      const fromHeaderIds = Array.isArray(cartHeader?.selected_fee_ids) ? cartHeader.selected_fee_ids : [];
      const merged = Array.from(new Set([...(ids || []), ...fromHeaderIds.map(String)]));
      return merged.length ? merged : undefined;
    }
  }, [isEdit, editOrder, cartHeader]);

  const isShippingOptionsReady =
    Boolean(sellerOrganizationId) &&
    (!isEdit ||
      Boolean(editOrder?.id && taxAddressObj?.address_details?.addressLine1));

  const [initialShippingSelection, setInitialShippingSelection] =
    React.useState<InitialShippingSelection | null>(null);

  React.useEffect(() => {
    setInitialShippingSelection(null);
  }, [resumeOrderId]);

  React.useEffect(() => {
    if (!isEdit || !editOrder?.id) return;
    const selection = getInitialShippingSelectionFromOrder(editOrder);
    if (selection) setInitialShippingSelection(selection);
  }, [isEdit, editOrder?.id]);

  const savedShippingRowsForEdit = React.useMemo(
    () => (isEdit && editOrder ? mapOrderShippingRowsForPicker(editOrder) : null),
    [isEdit, editOrder?.id]
  );

  const savedSummary = React.useMemo(() => {
    if (isEdit) {
      const items = Array.isArray(editOrder?.items) ? editOrder.items : [];
      const sub = items.reduce((s: number, it: any) => s + Number(it.item_sub_total || 0), 0);
      const prodTax = items.reduce((s: number, it: any) =>
        s + Number(it?.item_tax_info?.total_product_tax_amount || it?.tax_amount || 0), 0);

      const shipRows = editOrder?.order_charges?.shipping_option ?? [];
      const feeRows = editOrder?.order_charges?.specialfee ?? [];
      const shipAmt = shipRows.reduce((s: number, r: any) => s + Number(r.shipping_amount || 0), 0);
      const shipTax = shipRows.reduce((s: number, r: any) => s + Number(r.shipping_tax_amount || 0), 0);
      const feeAmt = feeRows.reduce((s: number, f: any) => s + Number(f.specialfee_amount || 0), 0);
      const feeTax = feeRows.reduce((s: number, f: any) => s + Number(f.specialfee_tax_amount || 0), 0);

      const discount = Number(editOrder?.discount_amount || 0);
      const taxTotal = prodTax + shipTax + feeTax;

      return {
        subTotal: +sub.toFixed(2),
        discount: +discount.toFixed(2),
        shipping: +shipAmt.toFixed(2),
        special: +feeAmt.toFixed(2),
        tax: +taxTotal.toFixed(2),
        grand: +(sub + shipAmt + feeAmt + taxTotal).toFixed(2),
        discountCode: editOrder?.discount_code || "",
      };
    } else if (cartHeader) {
      // use server truth from cart
      const shipRows = cartHeader?.order_charges?.shipping_option ?? [];
      const feeRows = cartHeader?.order_charges?.specialfee ?? [];
      const shipAmt = shipRows.reduce((s: number, r: any) => s + Number(r.shipping_amount || 0), 0);
      const shipTax = shipRows.reduce((s: number, r: any) => s + Number(r.shipping_tax_amount || 0), 0);
      const feeAmt = feeRows.reduce((s: number, f: any) => s + Number(f.specialfee_amount || 0), 0);
      const feeTax = feeRows.reduce((s: number, f: any) => s + Number(f.specialfee_tax_amount || 0), 0);

      // prefer API-provided fields if present
      const sub = Number(cartHeader.subtotal_amount ?? 0);
      const discount = Number(cartHeader.discount_amount ?? 0);
      const tax = Number(cartHeader?.order_tax_summary?.over_all_tax_amount ?? (shipTax + feeTax)); // product tax may be 0 if manual
      const grand = Number(cartHeader.total_amount ?? (sub + shipAmt + feeAmt + tax));

      return {
        subTotal: +sub.toFixed(2),
        discount: +discount.toFixed(2),
        shipping: +shipAmt.toFixed(2),
        special: +feeAmt.toFixed(2),
        tax: +tax.toFixed(2),
        grand: +grand.toFixed(2),
        discountCode: cartHeader?.discount_code || "",
      };
    }
    return null;
  }, [isEdit, editOrder, cartHeader]);

  const hydratedRef = React.useRef(false);         // optional (for quick checks)
  const [hydrated, setHydrated] = React.useState(false);  // <- actual state we depend on

  return (
    <div className="teammember container-fluid container-1440">
      <div className=" col-md-12">
        <div className="">
          <h2 className="m-0 fw-bold addheaders mb-3">Checkout </h2>
        </div>
      </div>
      <div className="row">
        <div className="col-xl-8 mb-5">
          <form onSubmit={formik.handleSubmit}>
            <div className="d-flex justify-content-center">
              <div className="box-shadow shadow-border-3 col-md-12 px-3">
                <div className="">
                  <div className="row ms-2">
                    <div id="row-overlap">
                      <h6 className="mb-0 d-flex align-items-center order-tittle ms-0 mt-3 mb-3">
                        <span className="icon-chip chip-blue me-2">
                          <i className="bi bi-card-checklist" />
                        </span>
                        <span>Order Details</span>
                      </h6>
                    </div>

                    {/* User Type */}
                    <div className="col-md-6 mb-3" id="row-overlap">
                      <UIReactSelectPro
                        formik={formik}
                        name="userType"
                        label="Select User Type"
                        options={userOptions}
                        value={
                          userOptions?.find((opt: any) => opt.value === formik.values.userType) ||
                          null
                        }
                        onChange={(selectedOption: any) => {
                          const userType = selectedOption?.value;
                          formik.setFieldValue("userType", userType);

                          // âœ… Clear ALL downstream fields when user type changes
                          clearTeamMember();
                          clearOffice();
                          clearOfficeAgent();
                          clearAgent();
                          clearPersonAndAddresses();
                        }}
                        onBlur={formik.handleBlur}
                        isDisabled={!!editOrder}
                      />
                    </div>

                    {/* Team Member path (NEW FLOW: Office first) */}
                    {formik.values.userType === "teammember" ? (
                      <>
                        {/* 1. Office (Required - comes first) */}
                        <div className="col-md-6 mb-3" id="row-overlap">
                          <label className="form-label fw-semibold">
                            Select Office <span className="text-danger">*</span>
                          </label>
                          <AsyncPaginate
                            classNamePrefix="rs"
                            placeholder="Select office"
                            value={selectedOffice}
                            loadOptions={loadOfficeOptions}
                            onChange={(selected) => {
                              if (selected) {
                                // âœ… CASCADE RESET: Office â†’ Reset team member, agent, addresses
                                clearTeamMember();
                                clearOfficeAgent();
                                formik.setFieldValue("billingAddressId", "");
                                formik.setFieldValue("shippingAddressId", "");
                                setAddressObjects([]);
                                setSelectedBillingAddress(null);
                                setSelectedShippingAddress(null);
                                setSelectedOfficeBillingAddress(null);
                                setSelectedOfficeShippingAddress(null);

                                // Set office
                                formik.setFieldValue("officeId", selected?.value || "");
                                setSelectedOffice(selected);
                              } else {
                                clearOffice();
                                clearTeamMember();
                                clearOfficeAgent();
                                formik.setFieldValue("billingAddressId", "");
                                formik.setFieldValue("shippingAddressId", "");
                                setAddressObjects([]);
                                setSelectedBillingAddress(null);
                                setSelectedShippingAddress(null);
                                setSelectedOfficeBillingAddress(null);
                                setSelectedOfficeShippingAddress(null);
                              }
                            }}
                            onBlur={() => formik.setFieldTouched("officeId", true)}
                            additional={{ page: 1 }}
                            defaultOptions
                            debounceTimeout={300}
                            isClearable
                            isDisabled={!!editOrder}
                            components={{ DropdownIndicator }}
                            styles={customSelectStyles}
                          />
                          {formik.touched.officeId && formik.errors.officeId && (
                            <div className="text-danger mt-1">{formik.errors.officeId}</div>
                          )}
                        </div>

                        {/* 2. Team Member (Required - after office) */}
                        {selectedOffice && (
                          <div className="col-md-6 mb-3" id="row-overlap">
                            <label className="form-label fw-semibold">
                              Select Team Member <span className="text-danger">*</span>
                            </label>
                            <AsyncPaginate
                              key={`office-${selectedOffice?.value || "no-office"}`}
                              classNamePrefix="rs"
                              placeholder="Select team memberâ€¦"
                              value={selectedTeamMember}
                              loadOptions={loadTeamMemberOptions}
                              onChange={(selected) => {
                                if (selected) {
                                  // âœ… CASCADE RESET: Team Member â†’ Reset agent only (keep office addresses)
                                  clearOfficeAgent();

                                  // Set team member data
                                  formik.setFieldValue("teamMemberId", selected.value || "");
                                  formik.setFieldValue("teamMemberName", selected.label || "");
                                  setSelectedTeamMember(selected);
                                  formik.setFieldValue("userEmail", selected.email || "");
                                  formik.setFieldValue("userPhone", selected.phone || "");
                                  formik.setFieldValue("userFirstName", selected.firstName || "");
                                  formik.setFieldValue("userLastName", selected.lastName || "");
                                  void fetchAddresses({
                                    selectedTeamMember: selected,
                                    officeId: selectedOffice?.value,
                                    selectedOfficeAgent: null,
                                    selectedAgent: null,
                                  });
                                } else {
                                  clearTeamMember();
                                  clearOfficeAgent();
                                  formik.setFieldValue("userEmail", "");
                                  formik.setFieldValue("userPhone", "");
                                  formik.setFieldValue("userFirstName", "");
                                  formik.setFieldValue("userLastName", "");
                                }
                              }}
                              onBlur={() => formik.setFieldTouched("teamMemberId", true)}
                              additional={{ page: 1 }}
                              defaultOptions
                              debounceTimeout={300}
                              isClearable
                              isDisabled={!!editOrder}
                              components={{ DropdownIndicator }}
                              styles={customSelectStyles}
                            />
                            {formik.touched.teamMemberId && formik.errors.teamMemberId && (
                              <div className="text-danger mt-1">{formik.errors.teamMemberId}</div>
                            )}
                          </div>
                        )}

                        {/* 3. Agent (Optional - after team member) */}
                        {selectedOffice && selectedTeamMember && (
                          <div className="col-sm-6 mb-3" id="row-overlap">
                            <label className="form-label fw-semibold">
                              Select Agent
                            </label>
                            <AsyncPaginate
                              key={`office-agents-${selectedOffice?.value || "no-office"}`}
                              classNamePrefix="rs"
                              placeholder="Select agentâ€¦"
                              value={selectedOfficeAgent || null}
                              loadOptions={loadOfficeAgentOptions}
                              onChange={(selected: any) => {
                                if (selected) {
                                  // âœ… CASCADE RESET: Agent â†’ Reset addresses and fetch agent addresses
                                  formik.setFieldValue("billingAddressId", "");
                                  formik.setFieldValue("shippingAddressId", "");
                                  setAddressObjects([]);
                                  setSelectedBillingAddress(null);
                                  setSelectedShippingAddress(null);
                                  setSelectedOfficeBillingAddress(null);
                                  setSelectedOfficeShippingAddress(null);

                                  // Set office agent data
                                  formik.setFieldValue("officeAgentId", selected?.value);
                                  setSelectedOfficeAgent(selected);
                                  formik.setFieldValue("userFirstName", selected?.firstName || "");
                                  formik.setFieldValue("userLastName", selected?.lastName || "");
                                  formik.setFieldValue("userEmail", selected?.email || "");
                                  formik.setFieldValue("userPhone", selected?.phone || "");
                                  
                                  // Fetch agent membership addresses
                                  void fetchAddresses({
                                    selectedOfficeAgent: selected,
                                    selectedTeamMember,
                                    officeId: selectedOffice?.value,
                                  });
                                } else {
                                  // Clear agent and restore office addresses
                                  clearOfficeAgent();
                                  formik.setFieldValue("billingAddressId", "");
                                  formik.setFieldValue("shippingAddressId", "");
                                  setAddressObjects([]);
                                  setSelectedBillingAddress(null);
                                  setSelectedShippingAddress(null);
                                  setSelectedOfficeBillingAddress(null);
                                  setSelectedOfficeShippingAddress(null);

                                  // Restore team member data
                                  if (selectedTeamMember) {
                                    formik.setFieldValue("userFirstName", selectedTeamMember.firstName || "");
                                    formik.setFieldValue("userLastName", selectedTeamMember.lastName || "");
                                    formik.setFieldValue("userEmail", selectedTeamMember.email || "");
                                    formik.setFieldValue("userPhone", selectedTeamMember.phone || "");
                                    
                                    // Re-fetch office addresses for TM end user
                                    void fetchAddresses({
                                      selectedOfficeAgent: null,
                                      selectedAgent: null,
                                      selectedTeamMember,
                                      officeId: selectedOffice?.value,
                                    });
                                  }
                                }
                              }}
                              onBlur={() => formik.setFieldTouched("officeAgentId", true)}
                              additional={{ page: 1 }}
                              defaultOptions
                              debounceTimeout={300}
                              isClearable
                              isDisabled={!!editOrder}
                              components={{ DropdownIndicator }}
                              styles={customSelectStyles}
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      /* Agent path */
                      formik.values.userType === "agent" && (
                        <div className="col-sm-6 mb-3" id="row-overlap">
                          <label className="form-label fw-semibold">
                            Select Agent <span className="text-danger">*</span>
                          </label>
                          <AsyncPaginate
                            classNamePrefix="rs"
                            placeholder="Search agents..."
                            value={selectedAgent}
                            loadOptions={loadAgentOptions}
                            onChange={(selected) => {
                              if (selected) {
                                // âœ… Clear addresses first
                                formik.setFieldValue("billingAddressId", "");
                                formik.setFieldValue("shippingAddressId", "");
                                setAddressObjects([]);
                                setSelectedBillingAddress(null);
                                setSelectedShippingAddress(null);
                                setSelectedOfficeBillingAddress(null);
                                setSelectedOfficeShippingAddress(null);

                                // Then set agent data
                                setSelectedAgent(selected);
                                formik.setFieldValue("agent_id", selected.value || "");
                                formik.setFieldValue("userFirstName", selected.firstName || "");
                                formik.setFieldValue("userLastName", selected.lastName || "");
                                formik.setFieldValue("userEmail", selected.email || "");
                                formik.setFieldValue("userPhone", selected.phone || "");
                              } else {
                                clearAgent();
                                clearPersonAndAddresses();
                              }
                            }}
                            onBlur={() => formik.setFieldTouched("agent_id", true)}
                            additional={{ page: 1 }}
                            defaultOptions
                            debounceTimeout={300}
                            isClearable
                            isDisabled={!!editOrder}
                            components={{ DropdownIndicator }}
                            styles={customSelectStyles}
                          />
                          {formik.touched.agent_id && formik.errors.agent_id && (
                            <div className="text-danger mt-1">{formik.errors.agent_id}</div>
                          )}
                        </div>
                      )
                    )}

                    {/* First Name */}
                    <div className="col-md-6 mb-3" id="row-overlap">
                      <UIInputPro
                        formik={formik}
                        name="userFirstName"
                        label={
                          <>
                            First Name <span className="text-danger">*</span>
                          </>
                        }
                        placeholder="Enter first name"
                        value={formik.values.userFirstName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      // readOnly={!isFieldEditable(order?.order_status ?? 0, "BasicDetails")}
                      />
                    </div>

                    {/* Last Name */}
                    <div className="col-md-6 mb-3" id="row-overlap">
                      <UIInputPro
                        formik={formik}
                        name="userLastName"
                        label={
                          <>
                            Last Name <span className="text-danger">*</span>
                          </>
                        }
                        placeholder="Enter last name"
                        value={formik.values.userLastName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      // readOnly={!isFieldEditable(order?.order_status ?? 0, "BasicDetails")}
                      />
                    </div>

                    {/* Email */}
                    <div className="col-md-6 mb-3" id="row-overlap">
                      <UIInputPro
                        formik={formik}
                        name="userEmail"
                        label={
                          <>
                            Email <span className="text-danger">*</span>
                          </>
                        }
                        placeholder="Enter email"
                        type="email"
                        value={formik.values.userEmail}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      // readOnly={!isFieldEditable(order?.order_status ?? 0, "BasicDetails")}
                      />
                    </div>

                    {/* Phone */}
                    <div className="col-md-6 mb-3" id="row-overlap">
                      <UIInputPro
                        formik={formik}
                        name="userPhone"
                        label={
                          <>
                            Phone Number <span className="text-danger">*</span>
                          </>
                        }
                        placeholder="Enter phone"
                        value={formik.values.userPhone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      // readOnly={!isFieldEditable(order?.order_status ?? 0, "BasicDetails")}
                      />
                    </div>
                  </div>
                </div>
                <hr className="mt-3" />
                <div className="row mb-2 mt-2">
                  <div className="my-2" id="row-overlap">
                    <h5 className="mb-4 table-headers ms-3">Billing Information</h5>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="align-items-center d-flex form-check gap-1 mb-0 ms-3">
                        <input
                          type="checkbox"
                          id="isSameAsBilling"
                          name="isSameAsBilling"
                          className="form-check-input me-2"
                          checked={formik.values.isSameAsBilling}
                          onChange={(e) => {
                            formik.handleChange(e);
                            const isChecked = e.target.checked;

                            if (isChecked) {
                              const billingId = formik.values.billingAddressId;
                              formik.setFieldValue("shippingAddressId", billingId);
                              formik.setFieldValue("isSameAsBilling", true);
                              checkProceedEligibility(billingId, billingId);
                            } else {
                              formik.setFieldValue("shippingAddressId", "");
                              formik.setFieldValue("isSameAsBilling", false);
                              setSelectedShippingAddress(null);
                              checkProceedEligibility(
                                formik.values.billingAddressId,
                                ""
                              );
                            }
                          }}
                          onBlur={formik.handleBlur}
                        />

                        <label className="form-check-label" htmlFor="isSameAsBilling">
                          Is Billing and Shipping address the same
                        </label>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary  table-addbutton"
                        onClick={() => setShowAddressForm(true)}
                      // disabled={!!selectedBillingAddress || !!selectedOfficeBillingAddress}
                      >
                        + Address
                      </button>
                    </div>
                  </div>
                  <div className="col-md-6" id="row-overlap">
                    <div className="my-2 ms-3 ">
                      <UIReactSelectPro
                        formik={formik}
                        name="billingAddressId"
                        label={
                          <>
                            Select Billing Address <span className="text-danger">*</span>
                          </>
                        }
                        options={addressOptions}
                        value={
                          addressOptions?.find(
                            (option: any) => option.value === formik.values.billingAddressId
                          ) || null
                        }
                        placeholder="- Select -"
                        onChange={(selected: any) => {
                          formik.setFieldValue("billingAddressId", selected?.value);
                          const isOffice =
                            !selectedOfficeAgent &&
                            !selectedAgent &&
                            selectedOffice;

                          const selectedAddress = addressObjects?.find(
                            (addr: any) => addr._id === selected?.value
                          );

                          if (isOffice) {
                            // safeConsole.log("selectedOfficeBillingAddress", selectedAddress);
                            setSelectedOfficeBillingAddress(selectedAddress || null);
                          } else {
                            setSelectedBillingAddress(selectedAddress || null);
                          }

                          checkProceedEligibility(selected?.value, formik.values.shippingAddressId);
                          // + calculateTotals(selectedAddress);
                        }}

                      />
                    </div>
                  </div>

                  {!formik.values.isSameAsBilling && (
                    <div className="col-md-6 mt-3 mb-2" id="row-overlap">
                      <h5 className=" mt-2 table-headers"></h5>

                      <UIReactSelectPro
                        formik={formik}
                        name="shippingAddressId"
                        label={
                          <>
                            Select Shipping Address <span className="text-danger">*</span>
                          </>
                        }
                        options={addressOptions}
                        value={
                          addressOptions?.find(
                            (option: any) => option.value === formik.values.shippingAddressId
                          ) || null
                        }
                        placeholder="- Select -"
                        onChange={(selected: any) => {
                          formik.setFieldValue("shippingAddressId", selected?.value);
                          // safeConsole.log("shippingAddressId", selected);
                          setShowPaymentSection(false);

                          const isOffice =
                            !selectedOfficeAgent &&
                            !selectedAgent &&
                            selectedOffice;

                          const selectedAddress = addressObjects?.find(
                            (addr: any) => addr._id === selected?.value
                          );
                          // safeConsole.log("is Office" , isOffice);

                          if (isOffice) {
                            // safeConsole.log(selectedAddress ,"ifblocj");
                            // selected office shipping address
                            setSelectedOfficeShippingAddress(selectedAddress || null);
                          } else {
                            // safeConsole.log(selectedAddress ,"elseblocj");
                            // selected agent shipping address
                            setSelectedShippingAddress(selectedAddress || null);
                          }

                          checkProceedEligibility(formik.values.billingAddressId, selected?.value);
                        }}
                      />
                    </div>
                  )}
                </div>
                <hr className="mt-3" />

                <div className="card ms-1">
                  <div className="card-body">
                    <div className="table-responsive table-card rdt-desktop" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                      <div style={{ maxHeight: "400px", overflowY: "auto", overflowX: "auto" }}>
                        <div className=" bg-white rounded-top border-0 py-3">
                          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2">
                            <h6 className="mb-0 d-flex align-items-center order-tittle ">
                              <span
                                aria-hidden="true"
                                className="image-icons"
                                style={{
                                  backgroundColor: "#E17100",
                                  WebkitMaskImage: `url(${ProductSelection})`,
                                  maskImage: `url(${ProductSelection})`,
                                }}
                              />
                              <span className="ms-2">Product Details</span>
                            </h6>
                          </div>
                        </div>
                        <table
                          className="table align-middle table-borderless mb-0 table-fixed checkout-product-table"
                          style={{ minWidth: "1100px", width: "100%" }}
                        >
                          <colgroup>
                            <col style={{ width: "26%" }} />
                            <col style={{ width: "9%" }} />
                            <col style={{ width: "11%" }} />
                            <col style={{ width: "9%" }} />
                            <col style={{ width: "10%" }} />
                            <col style={{ width: "8%" }} />
                            {/* <col style={{ width: "12%" }} /> */}
                            <col style={{ width: "15%" }} />
                          </colgroup>
                          <thead className="table-light text-center" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                            <tr className="table-light">
                              <th className="table-headers text-center" style={{ minWidth: "260px", whiteSpace: "normal", lineHeight: 1.25 }}>Product Details</th>
                              <th className="table-headers text-center" style={{ minWidth: "90px", whiteSpace: "normal", lineHeight: 1.25 }}>Sku</th>
                              <th className="table-headers text-center" style={{ minWidth: "100px", whiteSpace: "normal", lineHeight: 1.25 }} title="Unit Price">Item Price</th>
                              <th className="table-headers text-center" style={{ minWidth: "88px", whiteSpace: "normal", lineHeight: 1.25 }}>Quantity</th>
                              <th className="table-headers text-center" style={{ minWidth: "90px", whiteSpace: "normal", lineHeight: 1.25 }}>Discount</th>
                              {/* <th className="table-headers text-center" style={{ minWidth: "72px", whiteSpace: "normal", lineHeight: 1.25 }}>Tax</th> */}
                              <th className="table-headers text-center" style={{ minWidth: "110px", whiteSpace: "normal", lineHeight: 1.25 }}>Total Amount</th>
                              <th className="table-headers text-center" style={{ minWidth: "48px", whiteSpace: "normal" }} aria-hidden="true" />
                            </tr>
                          </thead>
                          <tbody>
                            {(cartItems.length === 0 ? [{}] : cartItems).map((item: any, index: number) => {
                                //  safeConsole.log("item in details:", item);
                              // ===============================================
                              // âœ… SIMPLIFIED PER-ROW CALCULATION
                              // ===============================================
                              const num = (v: any) => Number(v || 0);
                              const r2 = (n: number) => Math.round(num(n) * 100) / 100;

                              const key = `${item.product_id}:${item.variant_id}`;
                              const skuKey = String(item.sku || "");
                              const qty = Math.max(1, num(item.quantity));
                              const unitRetail = num(item.unit_retail_price ?? item.unit_purchase_price);
                              const changedUnit = num(item.changed_unit_price ?? unitRetail);

                              const liveRowTaxRaw =
                                (skuKey && productTaxMap?.[skuKey] !== undefined ? productTaxMap[skuKey] : undefined) ??
                                productTaxMap?.[key];
                              const rowTax = Number.isFinite(liveRowTaxRaw) ? num(liveRowTaxRaw) : num(item?.tax_details?.tax_amount);

                              const { lineDiscount: rowDiscount, lineTotal: lineTotalWithTax } =
                                getAgentRealtorCheckoutLinePricing({
                                  item,
                                  qty,
                                  unitRetail,
                                  changedUnitPrice: changedUnit,
                                  lineKey: key,
                                  rowTax,
                                });

                              // Image: prefer customized design preview when design_id is present
                              const firstId = idOf(item?.images?.[0]);
                              const src =
                                (item?.design_id && item?.preview_url
                                  ? getImageSrc(item.preview_url, imageUrlMap) || buildImageUrl(item.preview_url)
                                  : "") ||
                                getImageSrc(firstId, imageUrlMap) ||
                                getImageSrc(item?.image_url, imageUrlMap) ||
                                buildImageUrl(firstId || item?.image_url) ||
                                "";

                              const srcOk =
                                !!src &&
                                src.trim() !== "" &&
                                !src.endsWith("/undefined") &&
                                !src.includes("/null") &&
                                !src.endsWith("/");
                              const showProductImg = srcOk ? src : "";

                              return (
                                <tr key={index}>
                                  <td className="table-cells align-middle" style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                                    <div className="d-flex gap-3 align-items-start">
                                      {/* Product Image */}
                                      <div style={{ width: "80px", height: "100px", flexShrink: 0 }}>
                                        <img
                                          src={showProductImg || imagecustomRounded}
                                          alt={item?.product_name || ""}
                                          className="img-fluid rounded border"
                                          style={{ objectFit: "contain", width: "100%", height: "100%" }}
                                          onError={(e) => {
                                            (e.currentTarget as HTMLImageElement).src = imagecustomRounded;
                                          }}
                                        />
                                      </div>

                                      {/* Product Info */}
                                      <div>
                                        {(() => {
                                          const signageIdForProductLink = String(
                                            signageId ||
                                              supplierId ||
                                              (typeof window !== "undefined" ? localStorage.getItem("signageId") : "") ||
                                              (item as any)?.supplier_id ||
                                              ""
                                          ).trim();
                                          const productIdForLink = String(item?.product_id ?? "").trim();
                                          const isVariableLine = String(item?.product_type ?? "").toLowerCase() === "variable";
                                          const signageProductDetailHref =
                                            signageIdForProductLink && productIdForLink
                                              ? isVariableLine
                                                ? `${baseRoute}/viewSignageVariableProduct/${signageIdForProductLink}/${productIdForLink}`
                                                : `${baseRoute}/viewSignageProduct/${signageIdForProductLink}/${productIdForLink}`
                                              : "";
                                          const title = item.product_name || item.name || "-";
                                          return (
                                        <h5 className="fs-15 mb-1 wrap-anywhere">
                                          {signageProductDetailHref ? (
                                          <a
                                            href={signageProductDetailHref}
                                            className="link-primary wrap-anywhere"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            {title}
                                          </a>
                                          ) : (
                                            <span className="wrap-anywhere">{title}</span>
                                          )}
                                        </h5>
                                          );
                                        })()}

                                        {/* Custom fields */}
                                        {(() => {
                                          const fields = item?.customizations?.fields || [];
                                          const isFile = (f: any) => String(f?.field_type || "").toLowerCase() === "file";
                                          const firstFileVal = (raw: any): string => {
                                            if (!raw) return "";
                                            if (typeof raw === "string") return raw.trim();
                                            if (raw && typeof raw === "object" && raw.url) return String(raw.url).trim();
                                            return "";
                                          };
                                          const hasValue = (f: any): boolean => {
                                            if (isFile(f)) return !!firstFileVal(f?.value);
                                            return String(f?.value ?? "").trim().length > 0;
                                          };
                                          if (!fields.some(hasValue)) return null;
                                          const textFields = fields.filter((f: any) => !isFile(f) && hasValue(f));
                                          const fileFields = fields.filter((f: any) => isFile(f) && hasValue(f));

                                          return (
                                            <div className="custom-fields-container wrap-anywhere">
                                              {textFields.map((field: any, idx: number) => (
                                                <div
                                                  key={`${field?._id || field?.field_name || "cf"}-t-${idx}`}
                                                  className="mb-2"
                                                  style={{ wordBreak: "break-word", maxWidth: 400 }}
                                                >
                                                  <strong className="custom-cells">{field.field_name} :</strong>{" "}
                                                  <span className="wrap-anywhere">{String(field?.value ?? "").trim()}</span>
                                                </div>
                                              ))}
                                              {fileFields.map((field: any, idx: number) => {
                                                const id = firstFileVal(field?.value);
                                                const href = getImageSrc(id, imageUrlMap) || buildImageUrl(id);
                                                return (
                                                  <div
                                                    key={`${field?._id || field?.field_name || "cf"}-file-${idx}`}
                                                    className="mb-2"
                                                    style={{ wordBreak: "break-word", maxWidth: 400 }}
                                                  >
                                                    <div className="mb-1">{field.field_name}</div>
                                                    {href ? (
                                                      <a href={href} download target="_blank" rel="noopener noreferrer">
                                                        <img
                                                          src={href}
                                                          alt={field.field_name}
                                                          className="rounded"
                                                          style={{ maxWidth: 100, height: "auto", cursor: "pointer" }}
                                                        />
                                                      </a>
                                                    ) : /^[0-9a-f]{24}$/i.test(id) ? (
                                                      <span style={{ color: "#999" }}>Loadingâ€¦</span>
                                                    ) : null}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  </td>

                                  {/* SKU */}
                                  <td>
                                    <div className="fw-semibold text-center wrap-anywhere">
                                      {item?.sku || "-"}
                                    </div>
                                  </td>
                                   <td className="py-2 text-center align-middle">
                                    <div className="fw-semibold fs-6">${unitRetail.toFixed(2)}</div>
                                  </td>

                                  {/* Quantity */}
                                  <td className="py-2">
                                    <input
                                      type="number"
                                      className="form-control text-center"
                                      value={item?.quantity || 0}
                                      onChange={(e) => handleQuantityChange(index, e)}
                                      onKeyDown={(e) => {
                                        if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') e.preventDefault();
                                      }}
                                      disabled
                                    />
                                  </td>

                                  {/* <td className="py-2 text-center align-middle">
                                    <div className="fw-semibold fs-6">${changedPrice.toFixed(2)}</div>
                                  </td> */}

                                  <td className="text-center align-middle">
                                    <DiscountAmountDisplay amount={rowDiscount} />
                                  </td>

                                  {/* Tax column */}
                                  {/* <td className="text-center align-middle">
                                    <div className="fw-semibold">${rowTax.toFixed(2)}</div>
                                    <div className="small text-muted">{rowTaxPct.toFixed(2)}%</div>
                                  </td> */}

                                  {/* Total Amount = qty Ã— unit_retail_price + tax */}
                                  <td className="py-2 text-center align-middle">
                                    {/* <div className="text-muted small">${baseUnit.toFixed(2)} Ã— {qty}</div> */}
                                    <div className="fw-semibold fs-6">${lineTotalWithTax.toFixed(2)}</div>
                                  </td>

                                  {/* Row actions */}
                                  <td className="text-center" style={{ minWidth: "20px" }}>
                                    <div className="d-flex align-items-center justify-content-between gap-2">
                                      <div className="d-flex align-items-center gap-1">
                                        {/* {(cartItems.length === 0 || index === cartItems.length - 1) && (
                                          <button
                                            type="button"
                                            className="border-0 p-0"
                                            onClick={() => setPickerOpen(true)}
                                          >
                                            <i className="ri-add-line text-success fs-18"></i>
                                          </button>
                                        )} */}
                                        {cartItems.length > 1 && (
                                          <button
                                            type="button"
                                            className="border-0 p-0"
                                            onClick={() =>
                                              setCartItems(prev => prev.filter((_, i) => i !== index))
                                            }
                                          >
                                            <i className="ri-close-line text-danger fs-18"></i>
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>



                        </table>
                      </div>
                    </div>
                    <div className="rdt-mobile box-shadow-datatables shadow-border-2">
                      <MobileCardsOnly table={mobileTable} />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <InstructionsCreateCard
                      title="Instructions"
                      mode="instructions_files"
                      loginType="signage"
                      value={formik.values.notes}
                      onChange={(v) => formik.setFieldValue("notes", v, false)}  // live sync
                      onLocalInstructionFilesChange={handlePendingInstructionFilesChange}
                      placeholder="Add instructions here"
                      rows={4}
                      className="mt-3"
                      iconUrl={OrderNotes}
                      iconColor="#10B981"
                      iconBackground="#EAF7F1"
                    />
                  </div>
                  <div className="col-12 col-lg-6" id="row-overlap">
                    <div className="card box-shadow-datatables shadow-border-2">
                      <div className="ms-3 py-3">
                        <h6 className="mb-0 order-tittle">Order Totals</h6>
                      </div>
                      <div className="card-body">
                        <OrderDiscountCodeField
                          className="mb-2"
                          value={couponInput}
                          appliedCode={liveDiscountDisplay.code}
                          discountAmount={liveDiscountDisplay.amount}
                          voucherApplied={
                            liveDiscountDisplay.amount > 0 &&
                            Boolean(liveDiscountDisplay.code.trim())
                          }
                          disabled={isRecomputingSales}
                          error={couponResolveError}
                          onChange={setCouponInput}
                          onApply={(code) => {
                            const trimmed = code.trim();
                            if (!trimmed) return;
                            userClearedCouponRef.current = false;
                            setCouponInput(trimmed);
                            setLiveDiscountDisplay({ code: "", amount: 0 });
                            setPerItemCouponMap({});
                            void recomputeSalesRef.current?.({ force: true, discountCode: trimmed });
                          }}
                          onClear={() => {
                            setCouponInput("");
                            setCouponResolveError("");
                            userClearedCouponRef.current = true;
                            setLiveDiscountDisplay({ code: "", amount: 0 });
                            setPerItemCouponMap({});
                            void recomputeSalesRef.current?.({ force: true, discountCode: "" });
                          }}
                        />
                        {zeroOrNegativeMsg && (
                          <div className="text-danger mt-2">{zeroOrNegativeMsg}</div>
                        )}

                        {/* Show applied discount code if exists */}


                        <hr />



                        {/* âœ… SIMPLIFIED ORDER SUMMARY - Shows current UI state */}
                        <div className="d-flex justify-content-between py-1">
                          <span className="order-card-title">Sub Total:</span>
                          <span className="order-card-cells">${uiSubtotal.toFixed(2)}</span>
                        </div>

                        {/* Discount (always show, even if $0) */}
                        {uiDiscount > 0 && liveDiscountDisplay.code ? (
                          <div className="d-flex justify-content-between py-1 text-success">
                            <span className="order-card-title" style={{ color: "#00a63e" }}>Discount({liveDiscountDisplay.code}):</span>
                            <span className="order-card-cells" style={{ color: "#00a63e" }}>-${uiDiscount.toFixed(2)}</span>
                          </div>
                        ) : (
                          <div className="d-flex justify-content-between py-1 text-success">
                            <span className="order-card-title" style={{ color: "#00a63e" }}>Discount (coupon):</span>
                            <span className="order-card-cells" style={{ color: "#00a63e" }}>-$0.00</span>
                          </div>
                        )}

                        <div className="d-flex justify-content-between py-1 align-items-start gap-2">
                          <span className="order-card-title text-break" style={{ maxWidth: "62%" }}>
                            {shippingChargeTotalsLabel}
                          </span>
                          <span className="order-card-cells flex-shrink-0">${uiShipping.toFixed(2)}</span>
                        </div>

                        <div className="d-flex justify-content-between py-1 align-items-start gap-2">
                          <span
                            className="order-card-title text-break"
                            style={{ maxWidth: "62%" }}
                            title={specialFeeBreakdownDisplay.tooltip}
                          >
                            {specialFeeBreakdownDisplay.labelText}:
                          </span>
                          <span className="order-card-cells flex-shrink-0">${uiSpecialFee.toFixed(2)}</span>
                        </div>

                        <div className="py-1">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="order-card-title">Tax:</span>
                            <span className="order-card-cells">${uiTotalTax.toFixed(2)}</span>
                          </div>

                          {/* {showTaxDetails && (
                            <div id="tax-breakdown" className="border rounded-3 p-2 bg-light mt-1">
                              <div className="d-flex justify-content-between small mb-1">
                                <span>Products Tax</span>
                                <span className="order-card-cells">{money(productTaxToShow)}</span>
                              </div>
                              <div className="d-flex justify-content-between small mb-1">
                                <span>Shipping Tax</span>
                                <span className="order-card-cells">{money(shippingTaxToShow)}</span>
                              </div>
                              <div className="d-flex justify-content-between small">
                                <span>Special Fee Tax</span>
                                <span className="order-card-cells">{money(feeTaxToShow)}</span>
                              </div>
                            </div>
                          )} */}
                        </div>

                        <hr className="my-2" />
                        <div className="d-flex justify-content-between pt-1">
                          <strong className="order-card-title fs-19">Total (USD):</strong>
                          <span className="order-card-cells fw-semibold">${uiGrandTotal.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr />
                </div>
                <div className="row mb-3 rcust-margin-4">
                  <div className="col-md-12 d-flex justify-content-between align-items-center">
                    <button
                      className="btn back-btn surface-btn fw-bold fs-16"
                      type="button"
                      onClick={() => safeNavigate(-1)}
                    >
                      <i className="bi bi-chevron-left me-2" />
                      Back
                    </button>
                    <div className="row mb-3 rcust-margin-4">
                      <div className="col-md-12 mt-2 d-flex justify-content-between flex-column">
                        {/* {shippingOptionError && (
                      <p className="text-danger mb-2">
                        Please select a shipping option before proceeding.
                      </p>
                    )} */}
                        <div className="d-flex justify-content-between">
                          <div></div>
                          <button
                            type="button"
                            className="btn prim-btnary save-btn surface-btn fw-bold fs-16"
                            disabled={!canSaveOrder || isAnythingBusy || checkingCoupon}
                            // disabled={!canSaveOrder || isAnythingBusy}
                            onClick={onConfirmAndPay}
                            title={
                              checkingCoupon
                                ? "Validating coupon..."
                                :
                                isAnythingBusy && canSaveOrder
                                  ? "Processing..."
                                  : !canSaveOrder
                                    ? "Please fill email, phone, billing/shipping address, shipping option, and required fees"
                                    : ""
                            }
                          >
                            {checkingCoupon ? "Validating Coupon..." : loading ? "Submitting..." : isAnythingBusy ? "Processing..." : "Process To Pay"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {pickerOpen && (
              <ProductPickerModal
                open={true}
                onClose={closePicker}
                supplierId={sellerOrganizationId || supplierId}
                createdById={effectiveCouponCreatedById || ""}
                onAdd={handleAddFromPicker}
                studioBatchVariantPreviews={
                  Object.keys(pickerStudioBatchVariantPreviews).length
                    ? pickerStudioBatchVariantPreviews
                    : undefined
                }
              />
            )}
            {showAddressForm && (
              <AddressFormModal
                isOpen={true}
                onClose={() => {
                  setShowAddressForm(false);
                }}
                token={token || ""}
                owner={
                  selectedOfficeAgent ||
                  selectedAgent ||
                  selectedTeamMember ||
                  (membershipId ? { value: membershipId, label: userName || "" } : null)
                }
                membershipId={resolveMembershipAddressesRouteId({
                  selectedOfficeAgent,
                  selectedAgent,
                  selectedTeamMember,
                  sessionMembershipId: membershipId,
                })}
                officeId={selectedOffice?.value || null}
                createOnOffice={
                  !!selectedOffice &&
                  !selectedOfficeAgent &&
                  !selectedAgent
                }
                useType="billing"
                  userType={
                      selectedOfficeAgent || selectedAgent
                        ? "agent"
                        : selectedOffice
                          ? "office"
                          : undefined
                    }
                prefill={{
                  // Prefill from your existing parent formik values (same field names you used)
                  label_of_address: formik?.values?.label_of_address || "",
                  firstName: formik?.values?.firstName || "",
                  lastName: formik?.values?.lastName || "",
                  company: formik?.values?.company || "",
                  phone: formik?.values?.phone || "",
                  addressLine1: formik?.values?.addressLine1 || "",
                  addressLine2: formik?.values?.addressLine2 || "",
                  city: formik?.values?.city || "",
                  state: formik?.values?.state || "",
                  zipcode: formik?.values?.zipcode || "",
                  country: formik?.values?.country || "",
                }}
                onCreated={(createdRaw, option, useType) => {
                  // setAddressObjects((prev) => [createdRaw, ...(prev || [])]);
                  setAddressObjects((prev) => [createdRaw, ...(prev || [])]);  // correct

                  formik.setFieldValue(
                    useType === "billing" ? "billingAddressId" : "shippingAddressId",
                    option.value
                  );
                  const isOffice =
      !!selectedOffice &&
      !selectedOfficeAgent &&
      !selectedAgent;

                  if (useType === "billing") {
                    if (isOffice) {
                      setSelectedOfficeBillingAddress(createdRaw);
                    } else {
                      setSelectedBillingAddress(createdRaw?.address_details || null);
                    }
                  } else {
                    if (isOffice) {
                      setSelectedOfficeShippingAddress(createdRaw);
                    } else {
                      setSelectedShippingAddress(createdRaw?.address_details || null);
                    }
                  }

                  // 4) Mirror to shipping if same-as-billing is checked
                  if (useType === "billing" && formik.values.isSameAsBilling) {
                    formik.setFieldValue("shippingAddressId", option.value);
                    if (isOffice) {
                      setSelectedOfficeShippingAddress(createdRaw);
                    } else {
                      setSelectedShippingAddress(createdRaw?.address_details || null);
                    }
                  }
                }}
                afterCreateRefresh={() => {
                  fetchAddresses();
                }}
              />
            )}
          </form>
        </div>
        {

          <div className="col-lg-4">
            <div className="card-body">
              <PONumber
                mode="create"
                disabled={Boolean(String(new URLSearchParams(location.search).get("project_id") || "").trim())}
                className="mb-3"
                value={String(formik.values.po_number || "")}
                onChange={(v) => formik.setFieldValue("po_number", v)}
              />
              {/* SHIPPING OPTIONS (UPS + Manual) */}
              {isShippingOptionsReady ? (
              <ShippingOptions
                key={
                  isEdit
                    ? `shipping-edit-${resumeOrderId}-${editOrder?.id ?? "pending"}-${shippingAddressSig}-${shippingProductsKey}`
                    : `shipping-${sellerOrganizationId || "pending"}-${shippingProductsKey}`
                }
                ref={shipRef}
                token={token!}
                shippingAddress={taxAddressObj}
                totalQty={cartItems.reduce((s, r) => s + Number(r.quantity || 0), 0)}
                selectedProducts={selectedProductsWithDims}
                rateReloadKey={shippingProductsKey}
                productsFull={[]}
                createdById={effectiveCouponCreatedById || membershipId || ""}
                supplierId={sellerOrganizationId}
                taxStatus={editOrder?.order_tax_summary?.tax_status}
                initialSelectedIds={initialShippingSelection}
                savedShippingRows={savedShippingRowsForEdit}
                // de-duped state update (no render storms)
                onPayloadChange={handleShipPayloadChange}
              />
              ) : (
                <div className="card shadow-border-3 box-shadow">
                  <div className="card-body text-muted small">Loading shipping optionsâ€¦</div>
                </div>
              )}
            </div>

            {/* SPECIAL FEE PICKER */}
            {sellerOrganizationId ? (
            <SpecialFeePicker
              key={isEdit ? `specialfee-edit-${resumeOrderId}-${editOrder?.id ?? "pending"}` : `specialfee-${sellerOrganizationId}`}
              ref={feeRef}
              token={token!}
              personId={effectiveCouponCreatedById || membershipId || ""}
              supplierId={sellerOrganizationId}
              orderId={String(resumeOrderId || orderId || editOrder?.id || "").trim()}
              shippingAddress={taxAddressObj}
              initialSelectedIds={initialFeeIds}
              knownTaxType={activeTaxType} // child can decide how to compute; we still read rows/taxes
              isTaxable={true}
              statusOk={true}
              taxStatus={editOrder?.order_tax_summary?.tax_status}
              onPayloadChange={React.useCallback((p: FeePayload) => {
                feePayloadRef.current = p;
                setFeePayload(prev => {
                  const prevKey = JSON.stringify((prev?.specialfee ?? []).map(r => [r.specialfee_id, r.specialfee_amount, r.specialfee_tax_amount]));
                  const nextKey = JSON.stringify((p?.specialfee ?? []).map(r => [r.specialfee_id, r.specialfee_amount, r.specialfee_tax_amount]));
                  return (prevKey === nextKey) ? prev : p;
                });
                if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
                recomputeTimerRef.current = setTimeout(() => {
                  void recomputeSalesRef.current?.({ feePayload: p });
                }, 300);
              }, [])}
              onSourceFeesLoaded={(fees) => {
                specialFeeCatalogRef.current = fees;
              }}
            />
            ) : (
              <div className="card shadow-border-3 box-shadow">
                <div className="card-body text-muted small">Loading special feesâ€¦</div>
              </div>
            )}
            {showPaymentSection && (
              <>
                <div className="card border shadow-none">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="fw-semibold mb-0">Payment</h6><span>${totalAmount}</span>
                    </div>
                    <hr />
                    {stripePromise && clientSecret && (
                      <Elements stripe={stripePromise} options={{ clientSecret }}>
                        <div>
                          <h5 className="mb-1">Payment Selection</h5>
                          <p className="text-muted mb-4">Please select payment method</p>
                        </div>

                        <div className="row">
                          <div className="col-lg-6 col-md-8">
                            <div className="form-check card-radio">
                              <input
                                id="paymentMethodOnline"
                                name="paymentMethod"
                                type="radio"
                                className="form-check-input"
                                checked={selectedPaymentMethod === "online"}
                                onChange={() => setSelectedPaymentMethod("online")}
                              />
                              <label className="form-check-label" htmlFor="paymentMethodOnline">
                                <i className="ri-bank-card-fill align-bottom"></i> <span>Online Payment</span>
                              </label>
                            </div>
                          </div>
                        </div>

                        {selectedPaymentMethod === "online" ? (
                          <div className="collapse show mt-4">
                            <div className="">
                              <PaymentForm
                                supplierId={sellerOrganizationId || supplierId}
                                totalAmount={totalAmount}
                                // redirectUrl="/agent/orders"
                                redirectUrl={`/agent/orderDetails/${order?.id}`}
                                dueAmount={0}
                                orderId={orderId}
                                selectedPaymentMethod={selectedPaymentMethod}
                                onPaymentSuccess={async () => {
                                  const paidOrderId = orderId || order?.id || "";
                                  if (token && paidOrderId) {
                                    try {
                                      const freshOrder =
                                        (await fetchSalesOrderForPaidFinalize(
                                          token,
                                          paidOrderId
                                        )) ?? (order as Record<string, unknown> | undefined);
                                      if (freshOrder) {
                                        await finalizeSalesOrderTaxAfterPaid({
                                          token,
                                          orderId: paidOrderId,
                                          order: freshOrder,
                                        });
                                      }
                                    } catch (finalizeErr) {
                                      console.warn(
                                        "[Checkout] Tax finalize after payment failed (non-blocking):",
                                        finalizeErr
                                      );
                                    }
                                  }
                                  setActiveTab("pills-finish-tab");
                                }}
                                userId={
                                  resolvePaymentMethodsMembershipId(order) ||
                                  membershipId ||
                                  ""
                                }
                                userEmail={order?.customer_email || ""}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="d-flex align-items-start gap-3 mt-4">
                            {/* <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => {
                                handleTabCompletion("pills-payment-tab");
                                setActiveTab("pills-finish-tab");
                                completeOrder(selectedPaymentMethod);
                              }}
                            >
                              Complete Order
                            </button> */}
                          </div>
                        )}
                      </Elements>
                    )}
                    {/* Move variable declaration outside TSX */}
                    {(() => {
                      const userIdForPayment = order?.inventory_source === "office" ? order?.team_member_id : order?.agent_id;
                      return (
                        <PaymentMethods
                          orderId={order?.id || ""}
                          totalAmount={order?.total_amount || 0}
                          supplierId={order?.signage_id || ""}
                          id={userIdForPayment || ""}
                          ordertype="salesOrder"
                        />
                      );
                    })()}
                  </div>
                </div>
              </>
            )}

          </div>
        }
      </div >

      <CheckoutPaymentModal
        open={checkoutPaymentOpen}
        onClose={() => setCheckoutPaymentOpen(false)}
        token={token || ""}
        variant="realtor"
        order={checkoutPaymentOrder}
        totalAmount={Number(
          checkoutPaymentOrder?.total_amount ?? uiGrandTotal ?? totalAmount ?? 0
        )}
        supplierId={resolveSupplierOrganizationId(
          sellerOrganizationId || supplierId,
          checkoutPaymentOrder
        )}
        payerUserId={effectiveCouponCreatedById || userId || ""}
        payerEmail={formik.values.userEmail || ""}
        paidById={effectiveCouponCreatedById || userId || ""}
        paidByName={
          formik.values.userFirstName || formik.values.userLastName
            ? `${formik.values.userFirstName} ${formik.values.userLastName}`.trim()
            : userName || ""
        }
        onPaymentComplete={(orderId) => {
          setCheckoutPaymentOpen(false);
          if (orderId) {
            navigate(`${baseRoute}/orderDetails/${orderId}`);
          }
        }}
        onClearCart={() => {
          clearCheckoutCartAfterPayment({
            dispatch,
            clearCartItems,
            fetchCart,
          });
        }}
      />

      {/* Coupon Expiration Modal */}
      {CouponExpirationModal}
    </div >
  );
};

export default RealtorOrderCheckout;







//  <div className="d-flex justify-content-between py-1">
//                         <span className="order-card-title">Sub Total:</span>
//                         <span className="order-card-cells">{money(savedSummary ? savedSummary.subTotal : subTotal)}</span>
//                       </div>
//                       {/* {couponAmount > 0 && ( */}
//                         <div className="d-flex justify-content-between py-1 text-success">
//                           <span className="order-card-title ">Discount (coupon):</span>
//                           <span className="order-card-cells">-{money(savedSummary ? savedSummary.discount : 0)}</span>
//                         </div>
//                       {/* )} */}
//                       <div className="d-flex justify-content-between py-1">
//                         <span className="order-card-title">Shipping Charge:</span>
//                         <span className="order-card-cells">
//                           {money(shippingAmountToShow)}
//                         </span>
//                       </div>
//                       <div className="d-flex justify-content-between py-1">
//                         <span className="order-card-title">Special Fee:</span>
//                         <span className="order-card-cells">{money(savedSummary ? savedSummary.special : liveSpecialFee)}</span>
//                       </div>
//                       <div className="py-1">
//                         <div className="d-flex justify-content-between align-items-center">
//                           <button
//                             type="button"
//                             onClick={() => setShowTaxDetails(v => !v)}
//                             className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center"
//                             aria-expanded={showTaxDetails}
//                             aria-controls="tax-breakdown"
//                           >
//                             {/* <i className={`bi ${showTaxDetails ? "bi-chevron-down" : "bi-chevron-right"} me-1`} /> */}
//                             <span className="order-card-title">Tax :</span>
//                           </button>
//                           <span className="order-card-cells">
//                             <span>{money(totalTaxAll)}</span>
//                           </span>
//                         </div>

//                         {/* {showTaxDetails && (
//                           <div id="tax-breakdown" className="border rounded-3 p-2 bg-light mt-1">
//                             <div className="d-flex justify-content-between small mb-1">
//                               <span>Products Tax</span>
//                               <span className="order-card-cells">{money(productTaxToShow)}</span>
//                             </div>
//                             <div className="d-flex justify-content-between small mb-1">
//                               <span>Shipping Tax</span>
//                               <span className="order-card-cells">{money(shippingTaxToShow)}</span>
//                             </div>
//                             <div className="d-flex justify-content-between small">
//                               <span>Special Fee Tax</span>
//                               <span className="order-card-cells">{money(feeTaxToShow)}</span>
//                             </div>
//                           </div>
//                         )} */}
//                       </div>

//                       <hr className="my-2" />
//                       <div className="d-flex justify-content-between pt-1">
//                         <strong className="order-card-title fs-19">Total (USD):</strong>
//                         <span className="order-card-cells fw-semibold">{money(savedSummary ? savedSummary.grand : totalAmount)}</span>
//                       </div>

